"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import Link from "next/link"
import { motion, useReducedMotion } from "motion/react"

import { CheckCircle, AlertTriangle, Camera, ArrowRight, Loader2, Leaf, Lock, Images, X, Check, Bookmark, Home, ChevronDown, BookOpen, Search, Bell } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { AnalysisResultFrame } from "@/components/analysis/analysis-result-primitives"
import { ConfidenceMeter } from "./confidence-meter"
import {
  CONFIDENCE_SCORE_CAVEAT,
  isHighConfidence as isHighConfidenceScore,
  isLowConfidence as isLowConfidenceScore,
} from "@/lib/confidence-display"
import { EmailGate } from "./email-gate"
import { CarePlanSentSuccess } from "./care-plan-sent-success"
import { ResultPlanCard, getResultPlanMonth } from "./result-plan-card"
import { ResultStickySaveBar } from "./result-sticky-save-bar"
import { ResultProductPicks } from "./result-product-picks"
import { ResultConfidenceFeedback } from "./result-confidence-feedback"
import { ResultToolDeeplinks } from "./result-tool-deeplinks"
import { ScanQuotaBanner } from "./scan-quota-banner"
import { ResultCarePlanDigest } from "./result-care-plan-digest"
import {
  ResultSaveModal,
  type ResultSaveModalOpenedVia,
  type ResultSaveModalTrigger,
} from "./result-save-modal"
import type { RefinementPolicy, RefinementReason, RefinementTrigger } from "@/lib/analysis/refinement-policy"
import type { RefinementSummary } from "@/hooks/use-homepage-analysis"
import { regionDisplayName } from "@/lib/region-display"
import { buildTimelineUrl } from "@/lib/timeline-url"
import { isModifiedNavigationClick } from "@/lib/navigation/modified-click"
import { ResultQuickLinks } from "./result-quick-links"
import { LawnConfetti } from "@/components/shared/confetti"
import { SocialLoginButtons } from "@/components/auth/SocialLoginButtons"
import type { AnalysisResult } from "@/hooks/use-homepage-analysis"
import type { EmailSuggestion } from "@/lib/email-typo-detection"
import type { User } from "@supabase/supabase-js"
import { analytics, stashEmailCaptureAttribution, trackEvent, type ResultStateId } from "@/lib/analytics"
import { useOfferedActionsSnapshot } from "@/hooks/use-offered-actions-snapshot"
import { useInViewportImpression, useVisibleImpression } from "@/hooks/use-visible-impression"
import { AppDownloadCTA, AppDownloadTextLink, AppStoreLinkButton } from "@/components/ui/app-download-cta"
import { useAppInstallAvailable, useIsIosDevice } from "@/hooks/use-app-install-available"
import { GRASS_CHARACTERISTICS, getGrassCharacteristics } from "@/data/grass-characteristics"
import { GRASS_BLADES } from "@/components/guides/grass-blades"
import { grassTypeToGuideSlug } from "@/lib/grass-slug"
import { getGrassReferencePhotoMeta, getGrassReferenceImages } from "@/data/grass-reference-photos"
import type { WeatherData } from "@/lib/weather-utils"
import { ReferencePhotoGallery } from "./reference-photo-gallery"
import { ANONYMOUS_WEB_ANALYSIS_LIMITS, REGISTERED_FREE_ANALYSIS_LIMITS } from "@/lib/auth/feature-flags"
import { slugifyConditionName } from "@/data/condition-product-picks"
import { weedPageDisplayName } from "@/data/weeds/slugs"
import { isGrassFamilyWeedName } from "@/data/weeds/grass-family"
import { resolveWeedAlias } from "@/data/weed-registry"

// Evergreen capture guidance for the not-turfgrass / abstention result. These
// are universal "how to get a clean turf ID next time" instructions (not
// model-specific output), surfaced as a numbered card under the photo so the
// user leaves with a concrete recovery path instead of a dead end.
const NON_GRASS_CAPTURE_STEPS = [
  "One intact plant photographed from above, crown and stem branching visible",
  "A close, in-focus shot of the leaf collar where blade meets stem",
  "Any seedhead, or the growth pattern of the plant still in the ground",
]

// Kill switch — when NEXT_PUBLIC_HOMEPAGE_HARD_GATE_FALLBACK=true, render
// the legacy <EmailGate variant='cta'> path instead of the hybrid soft-entry
// layout. Lets us roll back without a code change if the post-deploy capture
// rate collapses.
const USE_LEGACY_HARD_GATE =
  process.env.NEXT_PUBLIC_HOMEPAGE_HARD_GATE_FALLBACK === 'true'

// Repeat taps on a refinement CTA inside this window are the same intent.
const REFINEMENT_TAP_GUARD_MS = 1200

// Module scope on purpose: the result stays mounted across client navigations
// and re-renders, and a per-mount ref would let a remount re-fire the same
// `attemptId:state` impression. Keyed exactly as the analytics contract asks.
const RESULT_STATE_IMPRESSIONS = new Set<string>()

// Hero OAuth pills. `cta_location` on result_primary_cta_clicked and `surface`
// on result_upgrade_prompted are the SAME string on purpose, so the seen→clicked
// join needs no translation table; `capture_layout` carries the arm on both.
const HERO_PILLS_CTA_LOCATION = "hero_pills"
const HERO_PILLS_CAPTURE_LAYOUT = "hero_pills_v1"
// Module scope for the same reason as RESULT_STATE_IMPRESSIONS: the result
// survives client navigation, and a per-mount ref would let a remount re-fire.
const HERO_PILLS_IMPRESSIONS = new Set<string>()

interface ResultsDisplayProps {
  analysisResult: AnalysisResult
  uploadedImage: string
  zipCode: string
  savedAnalysisId: string | null
  /** Entitlement for the paid continuation, supplied by the host: the
      saved-result page has no usage envelope, so `scanUsage` alone would
      sell Premium to an existing subscriber. Defaults leave the link
      visible, which is correct for the anonymous majority. */
  authIsPremium?: boolean
  subscriptionLoading?: boolean
  appCtaPlacement?: string
  user: User | null

  /** Funnel identity for `result_state_shown` / `result_next_action_clicked`.
   *  Optional so saved-result pages (which never ran an attempt in this tab)
   *  still instrument; the fallback below keys off the analysis instead. */
  attemptId?: string | null
  funnelId?: string | null

  // Email state
  carePlanEmail: string
  emailSuggestion: EmailSuggestion | null
  isSubmittingCarePlan: boolean
  // True once the anonymous email gate captured an email and is handing off to
  // the timeline. Swaps the gate/upgrade card for the on-page success frame so
  // the capture moment reads as the peak of the flow, not a bare redirect.
  emailGateCompleted?: boolean
  timelineRedirectUrl?: string

  // Handlers
  onReset: (event?: React.MouseEvent<HTMLElement>) => void
  /** Preserve the current photo and ZIP, then collect a supporting view for
      an explicit multi-photo refinement. Homepage-only; saved result pages
      fall back to the existing new-photo reset. */
  onRefineWithSupportingPhoto?: (reason?: RefinementTrigger) => void
  /** Anonymous visitor: the CTA renders in full, and the tap opens the
   *  sign-in gate instead of the photo step. This is the enticement moment,
   *  so the button is never hidden. */
  refinementLocked?: boolean
  /** True while the host has a refinement gate or the supporting-photo step
   *  open. The result stays mounted behind both, so the offer must not accept
   *  a second tap and start the same refinement twice. */
  refinementPending?: boolean
  /** Server's entitlement answer for this result. Absent on saved-result
   *  pages, where the local predicate below stands in. */
  refinementPolicy?: RefinementPolicy | null
  /** Fires once per result when a refinement offer renders, locked or not.
   *  Carries the trigger so the reason CTA and the default additive retry
   *  stay separable in the funnel. */
  onRefinementGateShown?: (trigger: RefinementTrigger) => void
  /** Present when THIS result came back from a refinement re-run. Drives the
   *  "Refined with N photos" confirmation. */
  refinementSummary?: RefinementSummary | null
  // Advance to the just-built timeline from the success frame's primary CTA.
  onSeeCarePlan?: () => void
  carePlanHref?: string
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmailKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onEmailSubmit: () => void
  onPrimaryCtaClick?: (method: "oauth" | "email") => void
  /** OAuth redirect fallback when timelineRedirectUrl is unavailable (e.g. saved
      results with no ZIP — without it the upgrade card renders no OAuth buttons). */
  oauthFallbackRedirectUrl?: string
  /** Scan quota from the analyze route; drives the anonymous scan unlock banner. */
  scanUsage?: { remaining: number; limit: number; isPremium: boolean } | null
  onQuotaWarningShown?: () => void
  onQuotaWarningClicked?: () => void
  /** Homepage-only honest impermanence cue ("not saved to an account"). The
      saved-result page omits it — that page IS the durable link. */
  showImpermanenceNote?: boolean
  onImpermanenceNoteClick?: () => void
  /**
   * Returning non-saver treatment (saved-result page): non-null
   * when THIS BROWSER has viewed this result before (localStorage counter, so
   * shared-link first-timers never see "welcome back"). `expiresInDays` is the
   * real 30-day anon-row cleanup deadline, or null for owned/verified rows —
   * the copy only names a deadline that exists. Suppressed internally for
   * signed-in or already-captured viewers.
   */
  returnVisit?: { visitCount: number; expiresInDays: number | null } | null
  onAcceptEmailSuggestion: () => void
  onDismissEmailSuggestion: () => void
  onSaveAndViewTimeline: () => void
  onViewTimeline: () => void
  onSelectAlternative: (grassType: string) => void
  onConfirmIdentity?: () => void

  // Identify → diagnose handoff for an unknown result whose visible subject is
  // a weed or other plant. Reuses the already-uploaded photo.
  onDiagnoseThisPhoto?: (event?: React.MouseEvent<HTMLElement>) => void
  diagnoseThisPhotoHref?: string
  onIdentifyWeedThisPhoto?: () => void
  isWeedRecoveryAvailable?: boolean
  isWeedRecoveryLoading?: boolean
  weedRecoveryFailed?: boolean
  weedRecoveryFunnelId?: string | null
  weedRecoveryCohort?: 'off' | 'internal' | '10' | '50' | '100'

  // Welcome-back nudge after a user returns from a guide page they reached
  // via the "Read our free guide" link on this very screen.
  returnedFromGuide?: { guideSlug: string } | null
  onDismissReturnedFromGuide?: () => void

  /** Rendered directly below the result summary grid, above the plan/upgrade
      sections. Saved-result pages use it for the seasonal week strip; the
      homepage flow leaves it unset. */
  belowGridSlot?: React.ReactNode

  /** Server-built week-ahead forecast, for surfaces that withhold the ZIP from
      the client (`/result/[id]`). Forwarded to the care-plan digest, which
      prefers it over its own client fetch. */
  weekAhead?: WeatherData | null
  /** Footer location label paired with `weekAhead`, e.g. "the Upper Midwest". */
  weekAheadLocationLabel?: string

  className?: string
}

interface AuthenticatedHeroPlanCtaProps {
  href: string
  isSaved: boolean
  isSubmitting: boolean
  onClick: () => void
  children: React.ReactNode
}

export function AuthenticatedHeroPlanCta({
  href,
  isSaved,
  isSubmitting,
  onClick,
  children,
}: AuthenticatedHeroPlanCtaProps) {
  const className = "w-full sm:w-auto bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white min-h-[48px] px-6 rounded-xl font-semibold text-[15px] shadow-lg hover:shadow-xl transition"

  if (isSaved) {
    return (
      <Button asChild className={className}>
        <Link href={href} onClick={onClick}>
          {children}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </Button>
    )
  }

  return (
    <Button onClick={onClick} disabled={isSubmitting} className={className}>
      {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
      {children}
      <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  )
}

export function ResultsDisplay({
  analysisResult,
  uploadedImage,
  zipCode,
  savedAnalysisId,
  authIsPremium = false,
  subscriptionLoading = false,
  appCtaPlacement = "homepage_result",
  user,
  attemptId,
  funnelId,
  carePlanEmail,
  emailSuggestion,
  isSubmittingCarePlan,
  emailGateCompleted,
  timelineRedirectUrl,
  onReset,
  onRefineWithSupportingPhoto,
  refinementLocked = false,
  refinementPending = false,
  refinementPolicy,
  onRefinementGateShown,
  refinementSummary,
  onSeeCarePlan,
  carePlanHref,
  onEmailChange,
  onEmailKeyPress,
  onEmailSubmit,
  onPrimaryCtaClick,
  oauthFallbackRedirectUrl,
  scanUsage,
  onQuotaWarningShown,
  onQuotaWarningClicked,
  showImpermanenceNote,
  onImpermanenceNoteClick,
  returnVisit,
  onAcceptEmailSuggestion,
  onDismissEmailSuggestion,
  onSaveAndViewTimeline,
  onViewTimeline,
  onSelectAlternative,
  onConfirmIdentity,
  onDiagnoseThisPhoto,
  diagnoseThisPhotoHref,
  onIdentifyWeedThisPhoto,
  isWeedRecoveryAvailable = false,
  isWeedRecoveryLoading = false,
  weedRecoveryFailed = false,
  weedRecoveryFunnelId,
  weedRecoveryCohort,
  returnedFromGuide,
  onDismissReturnedFromGuide,
  belowGridSlot,
  weekAhead,
  weekAheadLocationLabel,
  className,
}: ResultsDisplayProps) {
  const reduce = useReducedMotion()
  // State for celebration animations
  const [showConfetti, setShowConfetti] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  // Revealed when the user answers "Not quite" on the confidence-feedback tile,
  // so a wrong ID has an honest exit instead of a dead end.
  const [showFeedbackPicker, setShowFeedbackPicker] = useState(false)
  const [showUnknownPicker, setShowUnknownPicker] = useState(false)
  // Mobile-only: the raw model analysis is a ~10-line wall of text between the
  // result and the CTA. Collapse it to 3 lines on phones (full on desktop) so
  // the email ask isn't buried; desktop is unaffected (lg:line-clamp-none).
  const [descExpanded, setDescExpanded] = useState(false)
  const diagnosisRerouteShownRef = useRef<string | null>(null)
  const handlePostCaptureOAuthClick = () => {
    onPrimaryCtaClick?.("oauth")
  }

  const isRecaptureSubject =
    analysisResult.subjectGuess === 'not_plant' || analysisResult.subjectGuess === 'unreadable'
  const isStructuredAbstention =
    analysisResult.grassType === 'Unknown/Other' &&
    analysisResult.identificationStatus === 'abstained'
  // Check if this is a failed/error analysis. Subject-level abstentions always
  // go to recapture guidance, even if a fallback parser supplied a non-zero
  // confidence or an Unknown/Other grass label.
  const isFailedAnalysis = isRecaptureSubject || (!isStructuredAbstention && analysisResult.confidence === 0) ||
    analysisResult.grassType.includes('Issue') ||
    analysisResult.grassType.includes('Unclear') ||
    analysisResult.grassType.includes('Unavailable') ||
    analysisResult.grassType.includes('Not a Grass Image')
  // Honest displayed confidence — server-gated (margin / image quality /
  // region caps) when present, raw otherwise. Every band decision and every
  // user-facing "%" below reads this; RAW confidence is only used for the
  // failure sentinel above and analytics.
  const effectiveConfidence = analysisResult.displayConfidence ?? analysisResult.confidence
  const isUserSelectedIdentity = analysisResult.identitySource === 'user_selected'
  const registeredExtraIdentifyScans = Math.max(
    0,
    REGISTERED_FREE_ANALYSIS_LIMITS.identify - ANONYMOUS_WEB_ANALYSIS_LIMITS.identify,
  )

  // The model returns "Unknown/Other" when the photo doesn't match any of the
  // 10 supported grass types. It's not a failure (we still got a useful answer
  // and a list of regional alternatives), but it can't drive the normal care
  // plan flow either — every downstream surface assumes a real grass type.
  // Render the alternatives picker instead so the user can lock in the closest
  // match and continue.
  const isUnknownOther = analysisResult.grassType === 'Unknown/Other' && !isFailedAnalysis
  const unknownReason = analysisResult.abstentionReason
  const unknownTitle = unknownReason === 'poor_image'
    ? 'We need a clearer photo.'
    : unknownReason === 'insufficient_visual_evidence'
      ? 'We need one more view to separate these matches.'
      : unknownReason === 'unsupported_turfgrass'
        ? 'This may be a grass we do not support yet.'
        : unknownReason === 'non_grass_subject'
          ? 'This may not be turfgrass.'
          : "We couldn't pin down your grass."
  const unknownGuidance = analysisResult.recommendedNextPhoto ||
    (unknownReason === 'poor_image'
      ? 'Try a bright, in-focus close-up with several healthy blades filling the frame.'
      : unknownReason === 'insufficient_visual_evidence'
        ? 'Try a close-up that shows blade width, tips, and any runners or seedheads.'
        : 'Choose a match only if you recognize it, or take another diagnostic photo.')
  const deferUnknownPicker =
    unknownReason === 'poor_image' ||
    unknownReason === 'unsupported_turfgrass' ||
    unknownReason === 'non_grass_subject'
  // The server's policy wins when we have one; the local predicate is the
  // fallback for saved-result pages, which never call analyze.
  const canRefineWithSupportingPhoto = Boolean(onRefineWithSupportingPhoto) &&
    (refinementPolicy
      ? refinementPolicy.available
      : unknownReason === 'poor_image' || unknownReason === 'insufficient_visual_evidence')
  const possibleWeed = analysisResult.subjectGuess === 'grass' && Array.isArray(analysisResult.possibleWeeds)
    ? analysisResult.possibleWeeds.find((candidate) =>
        candidate &&
        typeof candidate.name === 'string' &&
        typeof candidate.confidence === 'number' &&
        candidate.confidence >= 40,
      )
    : undefined
  const isDiagnosisRerouteSubject =
    analysisResult.subjectGuess === 'weed' ||
    analysisResult.subjectGuess === 'other_plant' ||
    Boolean(possibleWeed)
  const showDiagnosisReroute =
    Boolean(onDiagnoseThisPhoto || onIdentifyWeedThisPhoto) &&
    isDiagnosisRerouteSubject &&
    (isFailedAnalysis || isUnknownOther)
  // The subject IS turf; the model only noticed a weed in the frame while it
  // abstained on the grass. The turf question stays primary here, so the weed
  // check renders as a secondary card and must not own the photo lane.
  const isMixedWeedReroute = showDiagnosisReroute && analysisResult.subjectGuess === 'grass' && Boolean(possibleWeed)
  // Only a reroute whose SUBJECT is the weed or plant replaces the refinement
  // offer. Gating every refinement site on the bare reroute left the mixed
  // case (Unknown/Other + a spotted weed) with no photo action at all for an
  // anonymous visitor: the entitled control below requires an account, and
  // the full retake spends a scan.
  const rerouteOwnsPhotoLane = showDiagnosisReroute && !isMixedWeedReroute

  // ── Reroute framing (2026-08, 492-result sample) ────────────────────────
  // 53.5% of reroute impressions name a weed that IS a grass (crabgrass,
  // foxtail, goosegrass, Poa annua, bentgrass, orchardgrass) at 80-100 raw
  // confidence on photos we rated Good. Answering a correct, confident read of
  // the user's real lawn with "This may not be turfgrass." frames a success as
  // a failure. Three branches now:
  //   grassy weed  → name it, say plainly that it is a grass, and keep the
  //                  user's original question (what is the turf?) alive
  //   other weed   → today's card and its 38.3% conversion, minus the
  //                  non-turf assertion, which 40% of the time we cannot make
  //   non-plant    → untouched; handled by the isRecaptureSubject early return
  //
  // The card names the model's `weedGuess`; the mixed case (turf photo with a
  // visible weed) names the `possibleWeeds` entry instead, which is the label
  // the card already leads with.
  const rerouteWeedName =
    (analysisResult.subjectGuess === 'grass' && possibleWeed ? possibleWeed.name : analysisResult.weedGuess) ?? null
  // Resolve through the weed registry's alias table, not a bare slugify. The
  // model emits common names that are not catalog slugs ("Foxtail" ->
  // foxtail-setaria, "Nutsedge" -> nutsedge-yellow, "Dandelion" -> dandelions),
  // and the naive form dropped BOTH the weed's name and its guide link for 69
  // of 362 named reroutes in a 28-day sample. Falls back to the old lookup so a
  // name the registry does not alias still behaves as before.
  const rerouteWeedLabel =
    rerouteWeedName &&
    (resolveWeedAlias(rerouteWeedName)?.guideSlug ||
      weedPageDisplayName(slugifyConditionName(rerouteWeedName)))
      ? rerouteWeedName
      : null
  // Branch (a): named, and the name is a plant in the grass family.
  const isGrassyWeedReroute =
    showDiagnosisReroute && Boolean(rerouteWeedLabel) && isGrassFamilyWeedName(rerouteWeedLabel)
  // Same guard the card's guide link uses: only a slug with a real /weeds page.
  const rerouteWeedGuideSlug = analysisResult.weedGuess
    ? resolveWeedAlias(analysisResult.weedGuess)?.guideSlug ||
      (weedPageDisplayName(slugifyConditionName(analysisResult.weedGuess))
        ? slugifyConditionName(analysisResult.weedGuess)
        : null)
    : null

  // The hero headline for a reroute. Branch (a) names the species; branch (b)
  // asks the one question that separates a genuine weed photo from a misread
  // of the lawn, instead of asserting a non-turf subject we cannot support on
  // 40% of this traffic (unnamed "weed" guesses).
  const heroUnknownTitle = isGrassyWeedReroute
    ? `That looks like ${rerouteWeedLabel}.`
    : showDiagnosisReroute && unknownReason === 'non_grass_subject'
      ? 'Is this a weed, or the lawn around it?'
      : unknownTitle
  const heroUnknownSubline = isGrassyWeedReroute
    ? `${rerouteWeedLabel} is a real grass, but it is a weed grass rather than a lawn turfgrass. That is why it did not match a turf type.`
    : rerouteOwnsPhotoLane
      ? "We stopped instead of forcing a regional guess. Here is what we found, and how to get a cleaner result."
      : unknownGuidance

  // Genuinely low (<50) gets a warning. The 50-69 band stays useful and
  // savable, but does not receive confirmation language or celebration.
  const isLowConfidence = isLowConfidenceScore(effectiveConfidence) && !isFailedAnalysis && !isUnknownOther && !isUserSelectedIdentity

  // Which CTA the visitor is looking at, for analytics and for the ask itself.
  const refinementReason: RefinementReason = refinementPolicy?.reason
    ?? (unknownReason === 'poor_image'
      ? 'poor_image'
      : unknownReason === 'insufficient_visual_evidence'
        ? 'inconclusive'
        : isUnknownOther
          ? 'picker_fallback'
          : 'low_margin')

  // Confirmation language starts at 70. Persistence and care-plan access
  // intentionally remain available from 50.
  // Excludes user-selected for the same reason the moderate and low bands do:
  // the score describes the species the MODEL named. A correction changes the
  // identity, not the number, so inheriting the band would let the user's pick
  // wear the model's "High Confidence" badge.
  const isHighConfidence = isHighConfidenceScore(effectiveConfidence) && !isFailedAnalysis && !isUnknownOther && !isUserSelectedIdentity
  const isCelebrationConfidence = effectiveConfidence >= 80 && isHighConfidence
  // Single gate for every surface that prints the model's number or the model's
  // photo evidence: the meter, the "% match" subheads, the why-block. A new "%"
  // site must read this rather than a band flag, or the leak comes back.
  const showsModelScore = !isUserSelectedIdentity && !isFailedAnalysis && !isUnknownOther
  // The model's original prediction, kept as labeled history under a corrected
  // identity. `confidence` is never reassigned on a correction, so a saved row
  // that predates `originalConfidence` still reads the right number.
  const originalModelScore = analysisResult.originalConfidence ?? effectiveConfidence
  const showsOriginalPrediction = Boolean(isUserSelectedIdentity && analysisResult.originalGrassType)
  // What a confirmation in the picker actually overwrites. The abstention
  // wording is the picker's own default and is only correct when the model
  // declined to name a species; on a positive prediction it must name the
  // prediction, and on an already-corrected result it must not call the user's
  // own pick a guess of ours.
  // Remount key for the "Does this look like your lawn?" widget. It exists to
  // reset the answer when the SPECIES changes, so 'user_confirmed' — which the
  // widget's own "Yes, that's it" sets — collapses back to 'model'. Keying on it
  // literally destroyed the confirmation card one tick after it rendered.
  const confidenceFeedbackKey = `${analysisResult.grassType}:${
    analysisResult.identitySource === 'user_confirmed'
      ? 'model'
      : analysisResult.identitySource ?? 'model'
  }`
  const pickerReplacementNote = isUserSelectedIdentity
    ? `This replaces ${analysisResult.grassType}, the grass you selected, for your care plan.`
    : `This replaces our ${effectiveConfidence}% ${analysisResult.grassType} guess for your care plan.`
  // Savable model ID in the 50-69 band. Gets the same evidence surfaces as a
  // confirmed match (reference gallery, "why this ID" reasoning) — a hedged
  // guess needs MORE verification material, not less — while confirmation
  // language and celebration stay 70+. Excludes user-selected: the model score
  // and reasoning describe the model's guess, not the user's pick.
  const isModerateConfidence =
    effectiveConfidence >= 50 && !isHighConfidence && !isFailedAnalysis && !isUnknownOther && !isUserSelectedIdentity

  // Side-by-side reference photo — only on confident IDs and only when we have
  // a curated reference for the exact species. Lets the user visually verify
  // the match (recognition over recall). Falls back to a single photo otherwise.
  const referencePhoto = isHighConfidence || isModerateConfidence || isUserSelectedIdentity
    ? getGrassReferencePhotoMeta(analysisResult.grassType)
    : null
  // Ordered gallery images (lead + curated alternates). Empty when there's no
  // reference; single-element for species with one photo.
  const referenceImages = isHighConfidence || isModerateConfidence || isUserSelectedIdentity
    ? getGrassReferenceImages(analysisResult.grassType)
    : []
  const displayedTips = analysisResult.enhancedTips?.length
    ? analysisResult.enhancedTips
    : analysisResult.tips

  // 2026-07-06: the high-confidence digest moved to the account-first "1a"
  // layout (Post Analysis Page design) — OAuth is THE ask, email is a quiet
  // reveal fallback inside `ResultCarePlanDigest`.

  // Check if we should show the care plan CTA
  const showCarePlanCTA = !isUnknownOther &&
    (effectiveConfidence >= 50 || isUserSelectedIdentity) &&
    !analysisResult.grassType.includes('Issue') &&
    !analysisResult.grassType.includes('Unclear') &&
    !analysisResult.grassType.includes('Unavailable') &&
    !analysisResult.grassType.includes('Not a Grass Image')

  // 2026-07-06 conversion pass: with the hybrid layout, only 30% of result
  // views ever scrolled the save ask into view and 0.7% interacted. Three
  // placement fixes target that: a CTA in the peak (hero) frame, compact hero
  // photos on mobile so the digest lands a half-scroll away, and a mobile
  // sticky save bar covering the dead zone. All gated to the anon
  // high-confidence hybrid path — authed users and the legacy hard gate keep
  // their existing frames.
  const showPeakCta =
    !user &&
    !USE_LEGACY_HARD_GATE &&
    !isUnknownOther &&
    isHighConfidence &&
    showCarePlanCTA &&
    !emailGateCompleted
  // 2026-07-11 coverage pass: the 07-06 peak CTA only covered anon +
  // high-confidence, leaving the other two savable tiers with a CTA-less hero
  // (PostHog: 91.5% of result viewers took no next action). Every hero with a
  // savable result now carries exactly one dominant action:
  //   - authed (any confidence)  → straight to the timeline (their plan is
  //     already built; scrolling them to a "save" ask would be a detour)
  //   - anon moderate-confidence → scroll to the below-grid upgrade ask, which
  //     owns #care-plan-upgrade in that layout
  const showAuthedHeroCta =
    Boolean(user) && !USE_LEGACY_HARD_GATE && !isUnknownOther && showCarePlanCTA
  const showModerateAnonHeroCta =
    !user &&
    !USE_LEGACY_HARD_GATE &&
    !isUnknownOther &&
    !isHighConfidence &&
    showCarePlanCTA &&
    !emailGateCompleted
  const entryPath = appCtaPlacement === "saved_result" ? "saved_result" : "homepage"

  // Returning non-saver treatment (hero line + return note): an anon viewer
  // re-opening a still-unsaved result is the most motivated non-saver we have.
  // Gated on the same savable tiers as the hero CTAs.
  const showReturnVisitTreatment =
    Boolean(returnVisit) &&
    !user &&
    !emailGateCompleted &&
    !isUnknownOther &&
    showCarePlanCTA
  // Deduped per result identity, not a one-way boolean — client navigation to
  // another /result/:id can reuse this component instance.
  const returnVisitShownRef = useRef<string | null>(null)
  useEffect(() => {
    const dedupKey = savedAnalysisId ?? analysisResult.image ?? "local"
    if (!showReturnVisitTreatment || returnVisitShownRef.current === dedupKey) return
    returnVisitShownRef.current = dedupKey
    trackEvent("result_return_visit_treatment_shown", {
      grass_type: analysisResult.grassType,
      confidence: analysisResult.confidence,
      entry_path: entryPath,
      visit_count: returnVisit?.visitCount,
      expires_in_days: returnVisit?.expiresInDays ?? undefined,
    })
  }, [showReturnVisitTreatment, analysisResult.grassType, analysisResult.confidence, analysisResult.image, entryPath, returnVisit, savedAnalysisId])
  // V1: peak-frame acknowledgment. Names the grass (specificity rule) and the
  // real deadline only when one exists (anon unverified rows expire in 30d).
  const returnVisitHeroLine = showReturnVisitTreatment ? (
    <p className="mt-3 inline-block rounded-lg bg-amber-50 px-3 py-1.5 text-[12.5px] font-medium leading-snug text-amber-800">
      Welcome back. Your {analysisResult.grassType} plan is still unsaved
      {typeof returnVisit?.expiresInDays === "number"
        ? ` and this link expires in ${returnVisit.expiresInDays} ${returnVisit.expiresInDays === 1 ? "day" : "days"}.`
        : "."}
    </p>
  ) : null
  const planTimelineHref = buildTimelineUrl({
    zipCode,
    grassType: analysisResult.grassType,
    savedAnalysisId,
    uploadedImageUrl: analysisResult.image,
  })

  // 2026-07-14 conversion pass: the anon hero CTA / sticky bar open the save
  // modal directly instead of smooth-scrolling to #care-plan-upgrade — the
  // scroll indirection forced a second decision on a second card (PostHog:
  // hero CTR ~2%, ask-viewer CTR 4.7%). The on-page digest card stays for
  // scroll-through users.
  const [saveModalTrigger, setSaveModalTrigger] = useState<ResultSaveModalTrigger | null>(null)
  // The hero holds two controls that open this one modal, so `trigger` alone
  // can no longer name the affordance. Undefined lets the modal derive it.
  const [saveModalOpenedVia, setSaveModalOpenedVia] = useState<ResultSaveModalOpenedVia | undefined>(
    undefined,
  )

  // If the email gate completes (from the modal or the on-page card), close
  // the modal so the success frame behind it is visible.
  useEffect(() => {
    if (emailGateCompleted) setSaveModalTrigger(null)
  }, [emailGateCompleted])

  const trackHeroCtaClick = (tier: "anon_high" | "anon_moderate" | "authed") => {
    emitNextAction(tier === "authed" ? "view_care_plan" : "save_care_plan")
    trackEvent("result_hero_cta_clicked", {
      grass_type: analysisResult.grassType,
      confidence: analysisResult.confidence,
      entry_path: entryPath,
      tier,
    })
    if (showReturnVisitTreatment && tier !== "authed") {
      trackEvent("result_return_visit_treatment_clicked", {
        grass_type: analysisResult.grassType,
        entry_path: entryPath,
        visit_count: returnVisit?.visitCount,
        surface: "hero_cta",
      })
    }
  }

  const handleHeroCtaClick = (tier: "anon_high" | "anon_moderate" | "authed") => {
    trackHeroCtaClick(tier)
    if (tier === "authed") {
      if (!savedAnalysisId) onSaveAndViewTimeline()
      return
    }
    setSaveModalTrigger("hero_cta")
  }

  useEffect(() => {
    if (!showDiagnosisReroute) return
    const key = `${savedAnalysisId ?? analysisResult.image}:${analysisResult.subjectGuess}:${possibleWeed?.name ?? ''}`
    if (diagnosisRerouteShownRef.current === key) return
    diagnosisRerouteShownRef.current = key
    trackEvent('identify_reroute_to_diagnosis_shown', {
      subject_guess: analysisResult.subjectGuess,
      weed_guess: analysisResult.weedGuess ?? undefined,
      possible_weed_guess: possibleWeed?.name,
      possible_weed_confidence: possibleWeed?.confidence,
    })
    if (isWeedRecoveryAvailable) {
      trackEvent('identify_to_weed_recovery_shown', {
        funnel_id: weedRecoveryFunnelId || undefined,
        analysis_id: savedAnalysisId ?? undefined,
        root_analysis_id: savedAnalysisId ?? undefined,
        platform: 'web',
        subject_guess: analysisResult.subjectGuess,
        possible_weed_guess: possibleWeed?.name,
        possible_weed_confidence: possibleWeed?.confidence,
        result_quality: analysisResult.quality?.imageQuality ?? analysisResult.imageQuality,
        model: analysisResult.model,
        prompt_version: analysisResult.quality?.promptVersion,
        pipeline_version: analysisResult.pipelineVersion,
        rollout_cohort: weedRecoveryCohort,
      })
    }
  }, [analysisResult.image, analysisResult.imageQuality, analysisResult.model, analysisResult.pipelineVersion, analysisResult.quality?.imageQuality, analysisResult.quality?.promptVersion, analysisResult.subjectGuess, analysisResult.weedGuess, isWeedRecoveryAvailable, possibleWeed, savedAnalysisId, showDiagnosisReroute, weedRecoveryCohort, weedRecoveryFunnelId])

  // Branch (a)'s second lane: the weed answer is correct, but the user asked
  // what their turf is. A supporting photo answers that when the policy allows
  // one; otherwise the honest route is a fresh photo of the lawn itself.
  const handleIdentifySurroundingTurf = (event?: React.MouseEvent<HTMLElement>) => {
    emitNextAction('identify_surrounding_turf')
    trackEvent('identify_reroute_surrounding_turf_tapped', {
      subject_guess: analysisResult.subjectGuess,
      weed_guess: rerouteWeedLabel ?? undefined,
      lane: canRefineWithSupportingPhoto ? 'refinement' : 'new_photo',
    })
    if (canRefineWithSupportingPhoto && !refinementPending) startRefinement(refinementReason)
    else onReset(event)
  }

  const handleDiagnosisReroute = (event?: React.MouseEvent<HTMLElement>) => {
    if (!onDiagnoseThisPhoto && !onIdentifyWeedThisPhoto) return
    emitNextAction(isWeedRecoveryAvailable ? 'identify_weed' : 'diagnose_photo')
    trackEvent('identify_reroute_to_diagnosis_tapped', {
      subject_guess: analysisResult.subjectGuess,
      weed_guess: analysisResult.weedGuess ?? undefined,
      possible_weed_guess: possibleWeed?.name,
      possible_weed_confidence: possibleWeed?.confidence,
    })
    if (isWeedRecoveryAvailable && onIdentifyWeedThisPhoto) onIdentifyWeedThisPhoto()
    else onDiagnoseThisPhoto?.(event)
  }

  // The weed lane already ran and failed. This is the diagnose fallback, a
  // distinct offered action from the `identify_weed` button above it.
  const handleDiagnoseThisPhotoFallback = (event?: React.MouseEvent<HTMLElement>) => {
    emitNextAction('diagnose_photo')
    onDiagnoseThisPhoto?.(event)
  }

  const handleSelectAlternative = (grassType: string) => {
    emitNextAction('pick_grass_type')
    setShowFeedbackPicker(false)
    onSelectAlternative(grassType)
  }

  const renderDiagnosisReroute = () => {
    if (!showDiagnosisReroute) return null
    // Specificity beats abstraction: name the weed when the model offered one.
    // Guard on the slug map, not truthiness — the enum's "Unknown weed" escape
    // (and any future value without a /weeds page) must fall back to the
    // generic label and never render a dead guide link.
    // Resolve the slug ONCE and derive both the guard and the href from it.
    // Deriving the href separately with a bare slugify sends an aliased name
    // ("Nutsedge" -> nutsedge-yellow) to a /weeds page that does not exist.
    const namedWeedSlug = analysisResult.weedGuess
      ? resolveWeedAlias(analysisResult.weedGuess)?.guideSlug ||
        (weedPageDisplayName(slugifyConditionName(analysisResult.weedGuess))
          ? slugifyConditionName(analysisResult.weedGuess)
          : null)
      : null
    const namedWeed = namedWeedSlug ? analysisResult.weedGuess : null
    const namedPossibleWeed =
      possibleWeed &&
      (resolveWeedAlias(possibleWeed.name)?.guideSlug ||
        weedPageDisplayName(slugifyConditionName(possibleWeed.name)))
        ? possibleWeed.name
        : null
    return (
      <div className="rounded-2xl border border-green-200 bg-linear-to-br from-green-50 to-emerald-50 p-6 text-left md:p-7">
        <div className="mb-2.5 flex flex-wrap items-center gap-2">
          <span className="text-[12px] font-bold uppercase tracking-[0.05em] text-green-700">
            {isMixedWeedReroute ? 'Also in this photo' : 'Best match'}
          </span>
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-[12px] font-semibold text-green-800">
            {isGrassyWeedReroute
              ? 'Grassy weed'
              : analysisResult.subjectGuess === 'other_plant'
                ? 'Other plant'
                : 'Lawn weed'}
          </span>
        </div>
        <h2 className="mb-2 text-[22px] font-bold leading-tight tracking-tight text-gray-900 md:text-2xl">
          {isMixedWeedReroute
            ? `We may see ${namedPossibleWeed ?? 'a weed'} in your lawn`
            : namedWeed
              ? `Looks like ${namedWeed}`
              : analysisResult.subjectGuess === 'other_plant'
                ? 'Looks like another plant'
                : 'Looks like a lawn weed'}
        </h2>
        <p className="mb-5 text-[15px] leading-relaxed text-gray-600">
          {isGrassyWeedReroute
            ? 'Confirm it here, then identify the turf around it.'
            : isMixedWeedReroute
              ? 'Your turf is still inconclusive. The same photo can also be checked for weeds, with no new upload.'
              : isWeedRecoveryAvailable
                ? 'Weed ID runs on this same photo. No second upload needed, and it does not count against your allowance.'
                : 'Diagnose the same photo for weeds, pests, or disease. No new upload needed.'}
        </p>
        {diagnoseThisPhotoHref && !isWeedRecoveryAvailable ? (
          <Button asChild className="min-h-[56px] w-full rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-[17px] font-bold text-white shadow-lg hover:from-green-700 hover:to-emerald-700 hover:shadow-xl">
            <Link href={diagnoseThisPhotoHref} onClick={handleDiagnosisReroute}>
              Diagnose this photo
              <ArrowRight className="ml-2 h-[18px] w-[18px]" aria-hidden="true" />
            </Link>
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleDiagnosisReroute}
            disabled={isWeedRecoveryLoading}
            className="min-h-[56px] w-full rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-[17px] font-bold text-white shadow-lg hover:from-green-700 hover:to-emerald-700 hover:shadow-xl"
          >
            {isWeedRecoveryLoading
              ? 'Identifying this weed...'
              : isWeedRecoveryAvailable
                ? isMixedWeedReroute
                  ? 'Check the weed in this photo'
                  : analysisResult.subjectGuess === 'other_plant'
                    ? 'Check this plant in Weed ID'
                    : 'Identify this weed'
                : 'Diagnose this photo'}
            <ArrowRight className="ml-2 h-[18px] w-[18px]" aria-hidden="true" />
          </Button>
        )}
        {isWeedRecoveryAvailable && weedRecoveryFailed && onDiagnoseThisPhoto ? (
          diagnoseThisPhotoHref ? (
            <Button asChild variant="outline" className="mt-2.5 min-h-[44px] w-full rounded-full border-green-700 text-green-800 hover:bg-white">
              <Link href={diagnoseThisPhotoHref} onClick={handleDiagnoseThisPhotoFallback}>
                Diagnose this photo instead
              </Link>
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={handleDiagnoseThisPhotoFallback}
              className="mt-2.5 min-h-[44px] w-full rounded-full border-green-700 text-green-800 hover:bg-white"
            >
              Diagnose this photo instead
            </Button>
          )
        ) : null}
        {/* Branch (a): a grassy weed is a correct read of a real grass, but it
            leaves the question the user actually asked unanswered. The turf
            path stays on the card at secondary weight so the weed lane (38.3%
            conversion) keeps the primary slot. */}
        {isGrassyWeedReroute && (
          <Button
            type="button"
            variant="outline"
            onClick={handleIdentifySurroundingTurf}
            className="mt-2.5 min-h-[44px] w-full rounded-full border-green-700 font-semibold text-green-800 hover:bg-white"
          >
            <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
            Identify the lawn around it
          </Button>
        )}
        {namedWeed && (
          <div className="mt-3.5 text-center">
            <Link
              href={`/weeds/${namedWeedSlug}`}
              onClick={() => {
                emitNextAction('weed_guide')
                trackEvent('weed_guess_guide_link_clicked', {
                  weed_guess: namedWeed,
                })
              }}
              className="inline-flex min-h-[36px] items-center px-2 text-[13px] font-medium text-green-700 underline underline-offset-[3px] hover:text-green-800"
            >
              {/* Single text node — the Link is inline-flex, which drops the
                  whitespace between separate JSX text children ("Crabgrassidentification"). */}
              {`Read the ${namedWeed} identification & control guide`}
            </Link>
          </div>
        )}
      </div>
    )
  }

  // Guide links on the ranked-candidate list are a content escape ramp for
  // users who don't want to sign up right now — turns a bounce into a
  // returning visitor. Reading a candidate's guide is also how someone talks
  // themselves into "Not quite", so the link stays informational and never
  // mutates the identified grass.
  const handleCandidateGuideClick = (candidateName: string, candidateSlug: string) => {
    trackEvent('results_guide_link_clicked', {
      grass_type: analysisResult.grassType,
      guide_slug: candidateSlug,
      link_context: 'ranked_candidate',
      candidate_grass: candidateName,
      authenticated: Boolean(user),
    })
    // Persist the analysis context so we can restore the results page when
    // the user comes back, and surface a "save your care plan" reminder.
    try {
      localStorage.setItem(
        'returnedFromGuide_pending',
        JSON.stringify({
          analysisResult,
          uploadedImageUrl: analysisResult.image,
          zipCode,
          savedAnalysisId,
          guideSlug: candidateSlug,
          savedAt: Date.now(),
        }),
      )
    } catch {
      /* localStorage unavailable */
    }
  }

  // Confetti is reserved for a strong 80+ result, not every savable result.
  useEffect(() => {
    if (isCelebrationConfidence && !hasAnimated) {
      // Small delay to let the component render first
      const timer = setTimeout(() => {
        setShowConfetti(true)
        setHasAnimated(true)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isCelebrationConfidence, hasAnimated])

  // Which refinement offer this result earns. The Unknown branch falls back to
  // a plain "Take another photo" reset when refinement is not available, and
  // that is a different offer, so it is not one of these.
  // Low-confidence results honor the server policy too: at the photo ceiling
  // the offer disappears rather than starting an impossible refinement. The
  // no-policy fallback (saved pages) stays permissive — the hop re-resolves.
  const canRefineLowConfidence = Boolean(onRefineWithSupportingPhoto) &&
    isLowConfidence &&
    (refinementPolicy ? refinementPolicy.available : true)
  // The 50-69 band: presented as "closest match", never confirmed, so the
  // confirm-it offer is honest there. Sub-70 is ~13% of identifies (60d) and
  // was previously the widest gap in refinement eligibility.
  const canRefineModerate = Boolean(onRefineWithSupportingPhoto) &&
    !isHighConfidence &&
    !isLowConfidence &&
    !isUnknownOther &&
    !isFailedAnalysis &&
    !isUserSelectedIdentity &&
    effectiveConfidence >= 50 &&
    (refinementPolicy ? refinementPolicy.available : true)
  // Two confident answers that disagree are the strongest reason to read both
  // photos together, so the offer must survive a high-confidence result. Only
  // adds a render site where no other refinement CTA already stands. A
  // user-selected identity retires the tie — the visitor already picked the
  // side, so there is nothing left for a tiebreaker photo to settle.
  const canRefineConflict = Boolean(onRefineWithSupportingPhoto) &&
    refinementPolicy?.reason === 'conflicting_rescan' &&
    refinementPolicy.available &&
    !isUnknownOther &&
    !isFailedAnalysis &&
    !isUserSelectedIdentity &&
    !canRefineLowConfidence &&
    !canRefineModerate
  const showsRefinementCta = Boolean(onRefineWithSupportingPhoto) &&
    ((isUnknownOther && canRefineWithSupportingPhoto) || canRefineLowConfidence || canRefineModerate || canRefineConflict)
  // Every refinement tap goes through here. The offer stays mounted while the
  // host opens its sign-in gate, so without a guard a double tap starts two
  // refinements and double-counts `refinement_started` against its own
  // impression denominator. The time window is the guard rather than a latch,
  // so dismissing the gate ("Keep my current result") leaves the CTA live.
  const lastRefinementStartRef = useRef(0)
  const startRefinement = (trigger: RefinementTrigger) => {
    if (refinementPending) return
    const now = Date.now()
    if (now - lastRefinementStartRef.current < REFINEMENT_TAP_GUARD_MS) return
    lastRefinementStartRef.current = now
    onRefineWithSupportingPhoto?.(trigger)
  }
  const handleRefine = () => {
    emitNextAction('refine_with_photo')
    startRefinement(refinementReason)
  }

  // Benefit-led, reason-specific label shared by every refinement CTA render
  // site. The words sell the outcome (what-vs-why).
  // The conflict arm sells a tiebreaker, not a re-read of both earlier photos:
  // the replacement rescan that produced the disagreement left photo one
  // behind, so the run that follows is this photo plus one new view.
  const refinementCtaBenefit = refinementReason === 'conflicting_rescan'
    ? 'Add one more angle to break the tie'
    : refinementReason === 'poor_image'
      ? 'Add a sharper photo to finish this ID'
      : refinementReason === 'low_margin'
        ? 'Add a close-up to confirm this ID'
        : 'Add a close-up to finish this ID'
  // The visible label sells the benefit; the lock glyph plus a screen-reader
  // suffix carry the account requirement.
  const refinementCtaLabel = refinementCtaBenefit
  // Benefit-led like the reason CTAs above it: the bare "Add another angle"
  // named the action and left the payoff inside the gate panel, which only an
  // anonymous tap ever opens.
  const retryAdditiveLabel = 'Add another angle for a sharper ID'

  // The default retry keeps photo 1 and asks for one more view; the full
  // restart stays available underneath it.
  const handleRetryAdditive = () => {
    emitNextAction('retry_additive')
    startRefinement('retry_additive')
  }
  // Redemption control for the "up to 3 photos per scan" promise on the states
  // that render no additive offer of their own. Lowest weight on the screen on
  // purpose: the reroute card's "Identify this weed" primary converts at 38.3%
  // and keeps its slot, with "Identify the lawn around it" under it, so this is
  // a text-weight third option rather than a third button competing with them.
  const renderEntitledAdditivePhoto = () => (
    <div className="mt-2 text-center" ref={refinementOfferRef}>
      <button
        type="button"
        onClick={handleRetryAdditive}
        disabled={refinementPending}
        data-testid="entitled-additive-photo"
        className="inline-flex min-h-[44px] items-center justify-center gap-1.5 px-3 text-[13px] font-semibold text-gray-500 transition-colors hover:text-gray-700 disabled:opacity-50"
      >
        <Camera className="h-3.5 w-3.5" aria-hidden="true" />
        {retryAdditiveLabel}
      </button>
      {/* The free-re-run promise requires a live policy: saved pages have no
          entitlement knowledge, and promising free on a spent root would be
          false. refinementsRemaining === null means unlimited (premium). */}
      {!refinementLocked && refinementPolicy != null && refinementPolicy.refinementsRemaining !== 0 && (
        <p className="mt-1.5 text-xs text-gray-500">This re-run is free. It will not use another scan.</p>
      )}
    </div>
  )
  const refinementAtCeiling = refinementPolicy != null &&
    refinementPolicy.slotsRemaining === 0 &&
    !refinementPolicy.requiresAuth &&
    !refinementPolicy.requiresUpgrade
  // Server truth: once the anonymous replacement allowance for this root is
  // spent, the only honest next step is the gated additive lane. A saved page
  // with no live policy keeps the offer up; the server cap is the backstop.
  const hideReplacementRetry = refinementLocked &&
    refinementPolicy?.replacementRescansRemaining === 0
  // `refinementAtCeiling` requires !requiresUpgrade, but a registered_free
  // caller at 3 of 3 photos HAS requiresUpgrade true (their cap is 3 while the
  // mode ceiling is 5), so it reads false and the offer rendered into a server
  // 403 `photo_limit_reached / photo_count`. Cap room is the honest test. A
  // saved page carries no policy and stays permissive, as before.
  // A LOCKED visitor is measured against the cap they get after signing in,
  // not their anonymous cap of 1: the offer they see is the account ask, and
  // the free tier reads up to 3. Testing 1 < 1 here removed the additive lane
  // from every confident anonymous result (live impressions fell from ~8/hour
  // to ~0 the hour it shipped) while the saved page, which carries no policy,
  // kept offering it.
  const hasPhotoCapRoom =
    refinementPolicy == null ||
    refinementLocked ||
    refinementPolicy.imageCount < refinementPolicy.photoCap
  const canRetryAdditive = Boolean(onRefineWithSupportingPhoto) &&
    !showsRefinementCta &&
    !refinementAtCeiling &&
    hasPhotoCapRoom
  // ENTITLEMENT, not worth-it. `refinementPolicy.available` answers "would
  // another photo move this answer?" and is false on a settled ID and on the
  // weed/plant reroute. The account asks on this screen (and in limit-walls)
  // separately promise a signed-in user "up to 3 photos per scan", which is a
  // PERMISSION claim, so it needs its own predicate — otherwise a visitor
  // signs in for the third photo and lands back on a result with no control
  // that spends it.
  //
  // A live policy is NOT required. The saved-result page threads
  // onRefineWithSupportingPhoto and ships no policy at all, and on its reroute
  // and Unknown/Other lanes every other refinement site is suppressed, so
  // demanding one left a signed-in owner with no refinement path even though
  // that page can navigate the lane with its result access token. Same
  // permissive-when-null precedent as `hasPhotoCapRoom` above; the server cap
  // is the backstop.
  //
  // `refinementsRemaining === 0` on its own is also not a refusal state. The
  // route 403s `additive_cap` only for a registered_free caller, and
  // resolveRefinementPolicy sets `requiresUpgrade` for exactly that caller
  // whenever the ceiling has room — which use-homepage-analysis intercepts with
  // the Premium wall before any request is sent. Hiding the control there turns
  // a monetization moment into a dead end. What is left to withhold is the
  // spent allowance that no gate would catch.
  const additiveAllowanceDeadEnd = refinementPolicy != null &&
    refinementPolicy.refinementsRemaining === 0 &&
    !refinementPolicy.requiresUpgrade
  const canAddEntitledPhoto = Boolean(onRefineWithSupportingPhoto) &&
    Boolean(user) &&
    hasPhotoCapRoom &&
    !additiveAllowanceDeadEnd
  // Every lane of the Unknown/Other column renders at most ONE refinement
  // offer. Hoisted out of `offeredActions` so the advertised set and the
  // entitlement control's de-dupe guard read the same predicate.
  const unknownPickerShown = !deferUnknownPicker || showUnknownPicker
  const unknownRefineRendered = unknownPickerShown
    ? canRefineWithSupportingPhoto && !rerouteOwnsPhotoLane
    : canRefineWithSupportingPhoto
  // The two states that promise three photos and offer no way to spend them:
  // the weed/plant reroute (server reason is null, so `available` is false and
  // every refinement site is additionally suppressed by `!rerouteOwnsPhotoLane`)
  // and the deferred Unknown/Other lanes. The success path already redeems the
  // promise through `canRetryAdditive`, and the whole bottom retry block it
  // lives in is gated `!isUnknownOther`, so these never double up.
  const showUnknownEntitledAdditive = isUnknownOther && canAddEntitledPhoto && !unknownRefineRendered
  const showFailedEntitledAdditive = isFailedAnalysis && showDiagnosisReroute && canAddEntitledPhoto
  const refinementChain = refinementPolicy?.chain ?? null

  // ── result_state_shown / result_next_action_clicked ─────────────────────
  // `grass_id_result_rendered` fires for every outcome, so "we named your
  // grass" and "we could not read your photo" were one number. These two
  // events split it by the state the user is actually looking at, and by what
  // that state offered them.
  //
  // attemptId falls back to the analysis identity: saved-result pages never
  // ran an attempt in this tab, and an impression keyed on nothing would
  // dedupe every result on the page against the first one.
  const resultAttemptId = attemptId ?? savedAnalysisId ?? analysisResult.image ?? 'unknown'
  const resultAuthState: 'authenticated' | 'anonymous' = user ? 'authenticated' : 'anonymous'
  // Guide link for the grass we actually identified (Task 3). Null for
  // Unknown/Other, failures, and any species with no guide page.
  const identifiedGuideSlug = !isUnknownOther && !isFailedAnalysis
    ? grassTypeToGuideSlug(analysisResult.grassType)
    : null
  const resultState: ResultStateId = isFailedAnalysis
    ? analysisResult.subjectGuess === 'unreadable'
      ? 'poor_image'
      : analysisResult.subjectGuess === 'not_plant' ||
        analysisResult.subjectGuess === 'weed' ||
        analysisResult.subjectGuess === 'other_plant'
        ? 'non_grass_subject'
        : 'inconclusive'
    : isUnknownOther
      ? unknownReason === 'poor_image'
        ? 'poor_image'
        : unknownReason === 'unsupported_turfgrass'
          ? 'unsupported'
          : unknownReason === 'non_grass_subject' || rerouteOwnsPhotoLane
            ? 'non_grass_subject'
            : 'inconclusive'
      : isLowConfidence
        ? 'low_confidence'
        : 'identified'
  // The one paid continuation this state has earned. A named species is only
  // useful on a calendar, so "we know your grass" continues into "we tell you
  // when to act on it" (Premium's zip+grass reminder schedule). Deliberately
  // scoped to the confirmed identity: sub-50, Unknown/Other and every failure
  // state have not established a species to schedule around, and showCarePlanCTA
  // already excludes them. It renders below the free product/tool value and
  // never above the save/email ask, which stays the top of the ladder.
  // Fail closed on entitlement. `scanUsage` is only populated by a fresh
  // analyze response, so /result/[id] has none and a bare `!isPremium` read
  // would sell Premium to an existing subscriber. Require a POSITIVE
  // known-not-premium signal instead: AuthContext must have settled the
  // subscription (it resolves immediately when signed out) and neither source
  // may say premium.
  const knownNotPremium =
    !subscriptionLoading && !authIsPremium && !scanUsage?.isPremium
  const showPremiumReminderContinuation =
    resultState === 'identified' &&
    showCarePlanCTA &&
    !USE_LEGACY_HARD_GATE &&
    !isUnknownOther &&
    // A user-selected identity keeps the model's original low score, and
    // showCarePlanCTA admits it at any confidence, so the sub-50 exclusion has
    // to be restated here or a 34% pick would be sold a reminder schedule.
    effectiveConfidence >= 50 &&
    knownNotPremium

  // The full-width block below the result grid (products, the tool grid, the
  // Premium line, the app card). Mirrors the render gate exactly.
  const showBelowGridSections = showCarePlanCTA && !USE_LEGACY_HARD_GATE && !isUnknownOther

  // iPhone-only app row at the top of a confirmed result. The bottom
  // AppDownloadCTA is reached by a small fraction of the phone traffic that
  // sees the result (147 of 1,100 iPhone Safari users over 28d), so the ask
  // moves into the peak frame instead of the edge-blind page bottom
  // (growth-design/signal-monetization: Peak-End + edge blindness).
  // Two gates, both from hooks/use-app-install-available.ts: the Android gate
  // (an App Store link dead-ends there) and an iOS-only gate, because desktop
  // keeps the bottom card and its QR handoff and has no use for a store link
  // in the hero.
  const appInstallAvailable = useAppInstallAvailable()
  const isIosDevice = useIsIosDevice()
  const showAppTopRow = resultState === 'identified' && appInstallAvailable && isIosDevice
  // Pinned placement: its own string so the read can separate this row from the
  // bottom card, which keeps `appCtaPlacement`.
  const appTopRowPlacement =
    appCtaPlacement === "saved_result" ? "saved_result_top" : "homepage_result_top"

  const premiumContinuationHref = funnelId ? `/pricing?funnel_id=${funnelId}` : '/pricing'

  // Mirrors the render gates below, in render order. Keep the two in step: the
  // whole point of the field is "what did we actually put in front of them".
  const offeredActions: string[] = (() => {
    const actions: string[] = []
    const pushRerouteActions = () => {
      if (!showDiagnosisReroute) return
      actions.push(isWeedRecoveryAvailable ? 'identify_weed' : 'diagnose_photo')
      // The weed lane can fail after it was offered. Its fallback button is a
      // second, separately rendered next step, so it needs its own entry or
      // every tap on it lands outside the offered set.
      if (isWeedRecoveryAvailable && weedRecoveryFailed && onDiagnoseThisPhoto) {
        actions.push('diagnose_photo')
      }
      if (isGrassyWeedReroute) actions.push('identify_surrounding_turf')
      if (rerouteWeedGuideSlug) actions.push('weed_guide')
    }
    if (isFailedAnalysis) {
      pushRerouteActions()
      if (showFailedEntitledAdditive) actions.push('retry_additive')
      actions.push('retake_photo')
      return actions
    }
    if (isUnknownOther) {
      if (rerouteOwnsPhotoLane) pushRerouteActions()
      if (unknownPickerShown) actions.push('pick_grass_type')
      else actions.push('browse_grass_types')
      // Each lane of this branch renders exactly one photo action: the
      // refinement when it is available (the picker lane also stands down for
      // a reroute that owns the lane), otherwise a full retake.
      actions.push(unknownRefineRendered ? 'refine_with_photo' : 'retake_photo')
      // The mixed reroute renders below the photo lane, so it is advertised
      // there too.
      if (isMixedWeedReroute) pushRerouteActions()
      // The entitlement control is a second, lower-weight photo action that
      // only appears where the refinement offer did not.
      if (showUnknownEntitledAdditive) actions.push('retry_additive')
      if (!user) actions.push('create_account')
      return actions
    }
    if (showPeakCta || showModerateAnonHeroCta) actions.push('save_care_plan')
    if (showAuthedHeroCta) actions.push('view_care_plan')
    if (showAppTopRow) actions.push('open_app')
    if (isLowConfidence) {
      actions.push('pick_grass_type')
      if (canRefineLowConfidence) actions.push('refine_with_photo')
      if (!user) actions.push('create_account')
    }
    if (identifiedGuideSlug) actions.push('open_grass_guide')
    if (canRefineModerate || canRefineConflict) actions.push('refine_with_photo')
    if (canRetryAdditive) actions.push('retry_additive')
    if (!hideReplacementRetry) actions.push('retake_photo')
    if (showBelowGridSections) actions.push('open_tool')
    if (showPremiumReminderContinuation) actions.push('premium_reminders')
    return actions
  })()

  // The list moves after the impression (the deferred Unknown/Other picker
  // swaps browse_grass_types for pick_grass_type, the weed lane can fail), so
  // both the impression and every click read the frozen copy.
  const { snapshotOfferedActions, positionOf } = useOfferedActionsSnapshot(offeredActions)

  const emitNextAction = (action: string) => {
    analytics.resultNextActionClicked({
      action,
      fromState: resultState,
      mode: 'identify',
      attemptId: resultAttemptId,
      funnelId,
      analysisId: savedAnalysisId,
      position: positionOf(action),
      authState: resultAuthState,
    })
  }

  // Every full-restart control on this screen. `retake_photo` is advertised in
  // offeredActions wherever one renders, so the two have to move together.
  const handleRetakePhoto = (event?: React.MouseEvent<HTMLElement>) => {
    emitNextAction('retake_photo')
    onReset(event)
  }

  useVisibleImpression(
    RESULT_STATE_IMPRESSIONS,
    true,
    `${resultAttemptId}:${resultState}`,
    () => {
      analytics.resultStateShown({
        state: resultState,
        mode: 'identify',
        attemptId: resultAttemptId,
        funnelId,
        analysisId: savedAnalysisId,
        offeredActions: snapshotOfferedActions(),
        confidence: analysisResult.confidence,
        displayConfidence: analysisResult.displayConfidence ?? analysisResult.confidence,
        grassType: analysisResult.grassType,
        restored: entryPath === 'saved_result',
        authState: resultAuthState,
      })
    },
  )

  // Impression for whichever refinement offer actually rendered — the reason
  // CTA or the default additive retry. Both lanes need a denominator, and the
  // trigger keeps them apart in the funnel. Keyed on the result so a rerender
  // cannot inflate it.
  // The additive lane renders from three different places, and each state owns
  // exactly one of them: the failed card and the Unknown/Other column both
  // suppress the bottom retry pair and redeem the promise through the entitled
  // control instead. A trigger set on a state that renders no offer would arm a
  // denominator no element can ever satisfy, so it resolves to null there.
  const rendersRetryAdditive = isFailedAnalysis
    ? showFailedEntitledAdditive
    : isUnknownOther
      ? showUnknownEntitledAdditive
      : canRetryAdditive
  // Every reason-CTA render site on the Unknown/Other column is additionally
  // suppressed by `!rerouteOwnsPhotoLane`, so eligibility alone overstates it:
  // the reroute lane redeems the offer through the entitled control above, and
  // that is the trigger the funnel should read there.
  const rendersReasonCta = showsRefinementCta &&
    (!isUnknownOther || canRefineLowConfidence || canRefineModerate || canRefineConflict || !rerouteOwnsPhotoLane)
  const refinementImpressionTrigger: RefinementTrigger | null = rendersReasonCta
    ? refinementReason
    : rendersRetryAdditive
      ? 'retry_additive'
      : null
  const refinementImpressionKey = refinementImpressionTrigger
    ? `${savedAnalysisId ?? analysisResult.grassType}:${refinementImpressionTrigger}`
    : null
  // Viewport-gated: the offer can sit hundreds of pixels below the fold, and a
  // mount-time impression counted every one of those as seen. The registry is
  // per-instance so a new result still re-arms it.
  const refinementImpressions = useRef<Set<string>>(new Set()).current
  const refinementOfferRef = useInViewportImpression(
    refinementImpressions,
    Boolean(refinementImpressionKey),
    refinementImpressionKey ?? '',
    () => {
      if (!refinementImpressionTrigger) return
      onRefinementGateShown?.(refinementImpressionTrigger)
    },
  )

  // Hero OAuth pills — the anon peak-frame ask. Every savable anonymous tier
  // renders exactly one of the three hero branches below, so this block is
  // built once and dropped into whichever one runs.
  const showHeroSavePills = showPeakCta || showModerateAnonHeroCta
  const heroSaveTier = showPeakCta ? "anon_high" : "anon_moderate"
  // A hand-picked ID is confirmed at any confidence; a model guess below the
  // high band is not, and the heading must not name a species we are hedging.
  const heroGrassNameIsConfirmed = isHighConfidence || isUserSelectedIdentity
  const heroSaveHeading = heroGrassNameIsConfirmed
    ? `Save my 12-month ${analysisResult.grassType} plan`
    : "Save my 12-month lawn plan"
  const saveFlowContext = {
    analysisId: savedAnalysisId ?? undefined,
    mode: "identify" as const,
    planType: "care" as const,
    outcome: "identified",
    entryPath,
    funnelId: funnelId ?? undefined,
    subject: analysisResult.grassType,
  }

  useVisibleImpression(
    HERO_PILLS_IMPRESSIONS,
    showHeroSavePills,
    `${resultAttemptId}:${HERO_PILLS_CAPTURE_LAYOUT}`,
    () => {
      analytics.resultUpgradePrompted({
        attemptId: resultAttemptId,
        grassType: analysisResult.grassType,
        mode: "identify",
        reason: "result_locked",
        surface: HERO_PILLS_CTA_LOCATION,
        captureLayout: HERO_PILLS_CAPTURE_LAYOUT,
      })
    },
  )

  // Emitted here rather than through the host's onPrimaryCtaClick: that handler
  // fires the same event without a location, so routing the pills through it
  // would double-count and leave them indistinguishable from the digest ask.
  const handleHeroSavePillClick = () => {
    trackHeroCtaClick(heroSaveTier)
    analytics.resultPrimaryCtaClicked({
      ctaLocation: HERO_PILLS_CTA_LOCATION,
      captureLayout: HERO_PILLS_CAPTURE_LAYOUT,
      attemptId: resultAttemptId,
      grassType: analysisResult.grassType,
      method: "oauth",
      confidence: analysisResult.confidence,
      entryPath,
      analysisId: savedAnalysisId ?? undefined,
      mode: "identify",
      planType: "care",
      outcome: "identified",
    })
    // Same source as the digest and the modal: the hero's own email lane is
    // that modal, and a separate source would split one capture family in two.
    stashEmailCaptureAttribution("grass_id_gate")
  }

  const handleHeroEmailLinkClick = () => {
    setSaveModalOpenedVia("email_link")
    handleHeroCtaClick(heroSaveTier)
  }

  const heroSavePills = showHeroSavePills ? (
    <div className="mt-4 md:mt-5 mx-auto w-full max-w-xs">
      <p className="text-[15px] font-extrabold leading-snug tracking-tight text-gray-900">
        {heroSaveHeading} <span className="text-green-700">&middot; free</span>
      </p>
      <div className="mt-3">
        <SocialLoginButtons
          redirectTo={timelineRedirectUrl ?? oauthFallbackRedirectUrl}
          savedPlanContext={saveFlowContext}
          pill
          appleSolid
          googleLabel="Continue with Google"
          appleLabel="Continue with Apple"
          onBeforeRedirect={handleHeroSavePillClick}
        />
      </div>
      <p className="mt-2.5 text-[12px] leading-snug text-gray-500">
        No credit card. No spam. &middot;{" "}
        <button
          type="button"
          onClick={handleHeroEmailLinkClick}
          className="font-semibold text-green-700 underline underline-offset-2 hover:text-green-800 transition-colors"
        >
          or get it by email
        </button>
      </p>
    </div>
  ) : null

  // A third pill under the sign-in pair, introduced by an "or" divider so it
  // reads as one more way to keep the result rather than a competing card.
  // The label names the benefit (growth-design cross-cutting rule 2) and the
  // app icon leads so it is recognized rather than read (rule 3).
  const appTopRow = showAppTopRow ? (
    <div data-testid="result-app-top-row" className="mx-auto mt-4 w-full max-w-md md:mt-5">
      <div className="flex items-center gap-3 px-2" aria-hidden="true">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">or on iPhone</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>
      <AppStoreLinkButton
        placement={appTopRowPlacement}
        label="Keep this result in the free app"
        onClick={() => emitNextAction('open_app')}
        className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-3 rounded-full border-2 border-green-600/30 bg-green-50 px-4 text-[16px] font-semibold text-green-900 transition-colors hover:bg-green-100"
      >
        <img
          src="/apple-touch-icon.png"
          alt=""
          width={28}
          height={28}
          loading="lazy"
          className="h-7 w-7 flex-shrink-0 rounded-[7px] shadow-[0_1px_3px_rgba(0,0,0,0.2)]"
        />
        <span>Keep this result in the free app</span>
      </AppStoreLinkButton>
    </div>
  ) : null

  // What the extra photo actually changed. Says nothing rather than inventing
  // a win when the outcome and the score both held.
  const refinementDeltaLine = (() => {
    if (!refinementSummary) return null
    const { previousOutcome, newOutcome, previousConfidence, newConfidence } = refinementSummary
    if (previousOutcome !== newOutcome && newOutcome === 'identified') {
      return 'The extra view was enough to confirm a match.'
    }
    if (previousOutcome !== newOutcome && newOutcome === 'unknown_other') {
      return 'Still no turfgrass match, even with the extra view.'
    }
    if (
      typeof previousConfidence === 'number' &&
      typeof newConfidence === 'number' &&
      Math.abs(newConfidence - previousConfidence) >= 5
    ) {
      return `Confidence moved from ${Math.round(previousConfidence)}% to ${Math.round(newConfidence)}%.`
    }
    if (previousOutcome === newOutcome) {
      return 'Both photos point the same way, which is itself a stronger read.'
    }
    return null
  })()

  // Failed-state branch: render a single centered card instead of entering the
  // 2-column grid. The grid leaves the right column empty when there's no care
  // plan CTA, which is the source of the off-center, "left-aligned" feel.
  if (isFailedAnalysis) {
    const recaptureTitle = analysisResult.subjectGuess === 'not_plant'
      ? "That photo doesn't look like lawn grass"
      : analysisResult.subjectGuess === 'unreadable'
        ? "We couldn't see enough grass detail"
        : "We couldn't analyze that photo"
    const recaptureBody = analysisResult.subjectGuess === 'not_plant'
      ? 'Retake the photo with living grass blades filling the frame. Avoid tools, labels, bare soil, and other objects.'
      : analysisResult.subjectGuess === 'unreadable'
        ? 'Retake it in bright, even light with the blades in focus. A close view and a wider lawn view work best.'
        : 'Something went wrong on our end. Please try again in a moment.'
    const handleBackToHome = (event: React.MouseEvent<HTMLElement>) => {
      onReset(event)
      if (!isModifiedNavigationClick(event) && typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    }

    return (
      <section
        id="results-section"
        className={cn("relative px-3 py-6 md:px-10 md:py-10 scroll-mt-24 overflow-x-hidden", className)}
      >
        <motion.div
          className="max-w-xl mx-auto"
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduce ? 0.3 : 0.45, ease: "easeOut" }}
        >
          <Card className="bg-white border border-gray-100 shadow-xs">
            <CardContent className="p-6 md:p-10">
              <p role="status" aria-live="assertive" className="sr-only">
                {recaptureTitle}. {recaptureBody}
              </p>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-red-50 border border-red-100 mb-4 md:mb-5">
                  <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-red-500" strokeWidth={2.25} />
                </div>

                <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  {recaptureTitle}
                </h1>
                <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed mb-6 max-w-md mx-auto">
                  {recaptureBody}
                </p>

                {uploadedImage && (
                  <div className="inline-block rounded-xl overflow-hidden border border-gray-200 mb-6">
                    <img
                      src={uploadedImage}
                      alt="Your grass photo"
                      className="w-20 h-20 md:w-24 md:h-24 object-cover"
                    />
                  </div>
                )}

                {showDiagnosisReroute && (
                  <div className="mb-6 max-w-md mx-auto">
                    {renderDiagnosisReroute()}
                    {showFailedEntitledAdditive && renderEntitledAdditivePhoto()}
                  </div>
                )}

                {displayedTips.length > 0 && (
                  <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 mb-6 text-left max-w-md mx-auto">
                    <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500 mb-2">
                      Before retrying
                    </p>
                    <ul className="space-y-1.5">
                      {displayedTips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 leading-snug">
                          <span className="text-gray-300 mt-1.5 text-xs leading-none">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 max-w-md mx-auto">
                  <Button
                    onClick={handleRetakePhoto}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white min-h-[44px] font-semibold"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Try a new photo
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleBackToHome}
                    className="flex-1 min-h-[44px] border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Back to home
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </section>
    )
  }

  // "Why this ID" collapsible — collapsed by default on every viewport. The %
  // is already in the hero subtitle, so recognition users need nothing here;
  // only skeptics expand for the confidence meter + reasoning. Rendered in two
  // breakpoint-gated slots: under the photo inside the left column on lg
  // (anchored to the evidence it explains), and as a third grid child on
  // mobile so the order stays photos → digest → why.
  // `showsModelScore` is redundant with the two band flags today; it is spelled
  // out so the gate stays self-documenting if a band flag is ever loosened.
  const renderWhyThisId = (showsModelScore && (isHighConfidence || isModerateConfidence)) ? (alwaysOpen: boolean) => {
    const analysisText = idReasoningFor(analysisResult.grassType, analysisResult.analysis)
    const paras = analysisParagraphs(analysisText)
    const open = alwaysOpen || descExpanded
    return (
      <>
        {alwaysOpen ? (
          // Desktop has the vertical room (tall sign-in card in the right
          // column), so the reasoning shows without a click.
          <div className="w-full rounded-xl border border-green-100 bg-green-50/60 px-4 py-3 text-left">
            <span className="text-[13px] font-semibold text-green-800 leading-snug">
              Why we&apos;re {effectiveConfidence}% sure it&apos;s {analysisResult.grassType}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setDescExpanded((v) => !v)}
            aria-expanded={descExpanded}
            aria-controls="grass-id-confidence-details"
            className="w-full flex items-center justify-between gap-2 rounded-xl border border-green-100 bg-green-50/60 px-4 py-3 text-left transition-colors hover:bg-green-50"
          >
            <span className="text-[13px] font-semibold text-green-800 leading-snug">
              Why we&apos;re {effectiveConfidence}% sure it&apos;s {analysisResult.grassType}
            </span>
            <ChevronDown
              className={cn(
                "w-4 h-4 flex-shrink-0 text-green-700 transition-transform",
                descExpanded && "rotate-180",
              )}
              aria-hidden="true"
            />
          </button>
        )}
        {/* Grid-rows expand/collapse — cheap (no JS measuring), animates both
            directions. Rendered unconditionally so the panel can animate closed;
            the global reduced-motion CSS rule zeroes the duration. */}
        <div
          id="grass-id-confidence-details"
          aria-hidden={!open}
          className={cn(
            "grid transition-[grid-template-rows] duration-300 ease-out",
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            <div className="mt-2 rounded-xl border border-gray-100 bg-white p-3 md:p-4">
              <ConfidenceMeter confidence={effectiveConfidence} caveat={CONFIDENCE_SCORE_CAVEAT} />
              {analysisResult.visibleEvidence && analysisResult.visibleEvidence.length > 0 ? (
                <ul className="mt-3 space-y-2">
                  {analysisResult.visibleEvidence.map((evidence) => (
                    <li key={evidence} className="flex gap-2 text-xs md:text-[13px] text-gray-700 leading-relaxed">
                      <CheckCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-600" aria-hidden="true" />
                      {evidence}
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="mt-2 space-y-2">
                {paras.map((para, i) => (
                  <p key={i} className="text-xs md:text-[13px] text-gray-600 leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  } : null

  return (
    <section id="results-section" className={cn("relative px-3 pt-3 pb-6 md:px-10 md:py-8 scroll-mt-24 overflow-x-hidden", className)}>
      {/* Celebration confetti for high-confidence identifications */}
      <LawnConfetti
        trigger={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />

      <AnalysisResultFrame cardClassName="border-gray-100 shadow-xs transition-opacity duration-300 ease-in-out">
            {/* Welcome-back banner — only when the user returned from a guide
                AND we have a savable result to nudge them toward */}
            {returnedFromGuide && showCarePlanCTA && (
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={reduce ? { duration: 0.3, ease: "easeOut" } : { duration: 0.4, ease: "easeOut", delay: 0.1 }}
                className={cn(
                  "relative mb-4 md:mb-6 rounded-xl border border-green-200/80",
                  "bg-linear-to-br from-green-50 via-green-50/80 to-emerald-50/60",
                  "shadow-[0_1px_2px_rgba(22,163,74,0.06),0_4px_12px_-4px_rgba(22,163,74,0.10)]",
                  "pl-3 md:pl-4 pr-10 md:pr-11 py-3",
                )}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full border border-green-200 bg-white text-green-600 shadow-xs">
                    <Bookmark className="w-2.5 h-2.5" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0 text-[10px] md:text-[11px] leading-snug">
                    <span className="font-semibold text-green-900">Welcome back.</span>{' '}
                    <span className="text-green-800/90">
                      Your{' '}
                      <span className="font-semibold text-green-900">{analysisResult.grassType}</span>{' '}
                      care plan is still here. Save it before you lose it.
                    </span>
                  </div>
                </div>
                {onDismissReturnedFromGuide && (
                  <button
                    type="button"
                    onClick={onDismissReturnedFromGuide}
                    className="absolute top-2 right-2 inline-flex items-center justify-center h-7 w-7 rounded-md text-green-700/70 hover:bg-green-100 hover:text-green-900 focus:outline-hidden focus:ring-2 focus:ring-green-500/30 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="w-4 h-4" strokeWidth={2.25} />
                  </button>
                )}
              </motion.div>
            )}

            {/* Screen-reader announcement of the identification outcome — the
                peak moment of the flow made accessible. The visual payoff is
                confetti + the match-found pill + the animated heading; none of
                that reaches assistive tech, so a polite live region speaks the
                result once when it lands. */}
            <p role="status" aria-live="polite" className="sr-only">
              {isUnknownOther
                ? `${heroUnknownTitle} ${heroUnknownSubline}`
                : isUserSelectedIdentity
                  ? `Using the grass you selected: ${analysisResult.grassType}.`
                : isHighConfidence
                  ? `Match found. Your grass is ${analysisResult.grassType}, ${effectiveConfidence} percent confidence. Your care plan is ready.`
                  : `Closest match: ${analysisResult.grassType}, ${effectiveConfidence} percent confidence.`}
            </p>

            {/* A refined result that looks identical to the first one reads as
                a wasted trip. Name the work that was done and what it moved,
                so the second photo feels earned rather than ignored. */}
            {refinementSummary && refinementSummary.imageCount > 1 && (
              <div className="mb-4 text-center">
                <div className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-green-700">
                  <Images className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
                  Refined with {refinementSummary.imageCount} photos
                </div>
                {refinementDeltaLine && (
                  <p className="mt-2 text-[13px] leading-snug text-gray-600">{refinementDeltaLine}</p>
                )}
              </div>
            )}

            {/* What each photo of this root actually returned. Recognition over
                recall: the disagreement was previously only visible by
                remembering the last screen. */}
            {refinementChain && refinementChain.length >= 2 && (
              <p className="mb-4 text-center text-[13px] leading-snug text-gray-600">
                {refinementChain
                  .map((entry) => `Photo ${entry.position}: ${entry.label}${entry.confidence != null ? ` ${entry.confidence}%` : ''}`)
                  .join(' \u00b7 ')}
              </p>
            )}

            {/* Header - success / unknown-other / low-confidence variants.
                Failed-state is handled by the early-return branch above. */}
            <div className="text-center mb-5 md:mb-8">
              {isUnknownOther ? (
                <>
                  {isGrassyWeedReroute ? (
                    <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-green-800 md:mb-4">
                      <Leaf className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
                      Grassy weed match
                    </div>
                  ) : (
                    <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-amber-800 md:mb-4">
                      <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
                      No turfgrass match
                    </div>
                  )}
                  <h1 className="text-2xl md:text-[2.25rem] font-extrabold tracking-tight text-gray-900 leading-[1.1]">
                    {heroUnknownTitle}
                  </h1>
                  <p className="mt-3 mx-auto max-w-2xl text-sm md:text-[17px] text-gray-600 leading-relaxed">
                    {heroUnknownSubline}
                  </p>
                  {/* Mobile-only: the verdict and the one action that fixes it
                      share a frame. Below lg the CTA column stacks under the
                      photo and capture-tips card, past the attention zone, so
                      the offer renders here instead (the column copies hide
                      themselves below lg). Desktop keeps the column placement. */}
                  {canRefineWithSupportingPhoto && !rerouteOwnsPhotoLane && (
                    <div className="mt-4 lg:hidden" ref={refinementOfferRef}>
                      <Button
                        type="button"
                        onClick={handleRefine}
                        disabled={refinementPending}
                        className="min-h-[56px] w-full max-w-sm rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-[16px] font-bold text-white shadow-lg hover:from-green-700 hover:to-emerald-700 hover:shadow-xl"
                      >
                        {refinementLocked ? (
                          <Lock className="mr-2 h-5 w-5" aria-hidden="true" />
                        ) : (
                          <Camera className="mr-2 h-5 w-5" aria-hidden="true" />
                        )}
                        {refinementCtaLabel}{refinementLocked && <span className="sr-only">, free account</span>}
                      </Button>
                      {/* The free-re-run promise requires a live policy: saved pages have no
                        entitlement knowledge, and promising free on a spent root would be
                        false. refinementsRemaining === null means unlimited (premium). */}
                    {!refinementLocked && refinementPolicy != null && refinementPolicy.refinementsRemaining !== 0 && (
                        <p className="mt-2 text-xs text-gray-500">This re-run is free. It will not use another scan.</p>
                      )}
                      {refinementLocked && (
                        <p className="mt-2 text-xs text-gray-500">Free account first. Your photo stays right here.</p>
                      )}
                    </div>
                  )}
                </>
              ) : isUserSelectedIdentity ? (
                <>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-blue-700 mb-3 md:mb-4">
                    <Check className="w-3 h-3" strokeWidth={3} />
                    You selected this match
                  </div>
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
                    Using{" "}
                    <span className="text-green-600 border-b-[3px] border-green-600 pb-0.5">
                      {analysisResult.grassType}.
                    </span>
                  </h1>
                  <p className="mt-2 md:mt-4 mx-auto max-w-2xl text-sm md:text-base text-gray-500 leading-relaxed">
                    Your selection replaces our original guess. The original model score was not changed.
                  </p>
                  {/* The original prediction as history, not as a claim about
                      the species on screen. Plain text on purpose: no meter, no
                      band color, nothing that reads as a score for this ID. */}
                  {showsOriginalPrediction && (
                    <p className="mt-2 mx-auto max-w-2xl text-[12.5px] text-gray-400 leading-snug">
                      Our model said {analysisResult.originalGrassType}, {originalModelScore}%.
                    </p>
                  )}
                  {returnVisitHeroLine}
                  {heroSavePills}
                  {showAuthedHeroCta && (
                    <div className="mt-4 md:mt-5">
                      <AuthenticatedHeroPlanCta
                        href={planTimelineHref}
                        isSaved={Boolean(savedAnalysisId)}
                        isSubmitting={isSubmittingCarePlan}
                        onClick={() => handleHeroCtaClick("authed")}
                      >
                        View my {analysisResult.grassType} plan
                      </AuthenticatedHeroPlanCta>
                    </div>
                  )}
                </>
              ) : isHighConfidence ? (
                <>
                  {/* Match-found pill — payoff moment, anchors the ID without being shouty */}
                  <div
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-green-700 mb-3 md:mb-4",
                      "animate-scale-bounce",
                    )}
                  >
                    <Check className="w-3 h-3" strokeWidth={3} />
                    Match found
                  </div>
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
                    That's{" "}
                    <span className="text-green-600 border-b-[3px] border-green-600 pb-0.5">
                      {analysisResult.grassType}.
                    </span>
                  </h1>
                  {/* One tight line. The confidence meter below carries the %,
                      and the digest carries the save ask — repeating either
                      here (or the old "just sign in to view it", which predates
                      the email path) was redundant. Region name is de-suffixed
                      so "Cool-season" doesn't appear twice in one sentence. */}
                  <p className="mt-2 md:mt-4 mx-auto max-w-2xl text-sm md:text-base lg:text-lg text-gray-500 leading-relaxed">
                    {seasonLabel(analysisResult.grassType)} grass, common across the{" "}
                    {regionDisplayName(analysisResult.region) || "region"} &middot;{" "}
                    {effectiveConfidence}% match
                    <span className="mt-1 block text-[11.5px] text-gray-400">
                      {CONFIDENCE_SCORE_CAVEAT}
                    </span>
                  </p>
                  {returnVisitHeroLine}
                  {/* Peak-frame ask — the celebration viewport carries the
                      whole decision (Peak-End: signal-monetization), so the
                      user never has to reach the digest to save. */}
                  {heroSavePills}
                  {showAuthedHeroCta && (
                    <div className="mt-4 md:mt-5">
                      <AuthenticatedHeroPlanCta
                        href={planTimelineHref}
                        isSaved={Boolean(savedAnalysisId)}
                        isSubmitting={isSubmittingCarePlan}
                        onClick={() => handleHeroCtaClick("authed")}
                      >
                        View my 12-month {analysisResult.grassType} plan
                      </AuthenticatedHeroPlanCta>
                      <p className="mt-2 text-[11.5px] text-gray-500 leading-snug">
                        This {getResultPlanMonth(analysisResult.grassType).monthName}&apos;s
                        tasks are ready &middot; Saved to your dashboard
                      </p>
                    </div>
                  )}
                </>
              ) : !isLowConfidence ? (
                <>
                  {/* Moderate band (50-69): the hedge lives in the verb
                      ("Looks like") and the visible % — the grass is still
                      named, with the same payoff structure as the confident
                      hero. Confirmation language ("Match found", "That's")
                      stays reserved for 70+. */}
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-green-700 mb-3 md:mb-4">
                    <Search className="w-3 h-3" strokeWidth={3} />
                    Closest match
                  </div>
                  <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
                    Looks like{" "}
                    <span className="text-green-600 border-b-[3px] border-green-600 pb-0.5">
                      {analysisResult.grassType}.
                    </span>
                  </h1>
                  <p className="mt-2 md:mt-4 mx-auto max-w-2xl text-sm md:text-base lg:text-lg text-gray-500 leading-relaxed">
                    {seasonLabel(analysisResult.grassType)} grass, common across the{" "}
                    {regionDisplayName(analysisResult.region) || "region"} &middot;{" "}
                    {effectiveConfidence}% match
                    <span className="mt-1 block text-[11.5px] text-gray-400">
                      {CONFIDENCE_SCORE_CAVEAT}
                    </span>
                  </p>
                  {returnVisitHeroLine}
                  {/* Moderate-confidence hero ask — same Peak-End coverage as
                      the high-confidence hero above; authed goes straight to
                      their plan instead. */}
                  {heroSavePills}
                  {showAuthedHeroCta && (
                    <div className="mt-3 md:mt-4">
                      <AuthenticatedHeroPlanCta
                        href={planTimelineHref}
                        isSaved={Boolean(savedAnalysisId)}
                        isSubmitting={isSubmittingCarePlan}
                        onClick={() => handleHeroCtaClick("authed")}
                      >
                        View my 12-month {analysisResult.grassType} plan
                      </AuthenticatedHeroPlanCta>
                      <p className="mt-2 text-[11.5px] text-gray-500 leading-snug">
                        This {getResultPlanMonth(analysisResult.grassType).monthName}&apos;s
                        tasks are ready &middot; Saved to your dashboard
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Genuinely low (<50): the hero claims no name — the
                      correction picker below owns the recovery path. */}
                  <div className="inline-flex items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-full mb-1 md:mb-2 bg-linear-to-br from-sky-500 to-blue-600">
                    <Leaf className="w-4 h-4 md:w-6 md:h-6 text-white" />
                  </div>
                  <h1 className="text-lg md:text-2xl font-bold text-gray-900">
                    Best match so far
                  </h1>
                  {returnVisitHeroLine}
                </>
              )}
              {appTopRow}
            </div>

            {/* Main Results Display — left column slightly wider so the photo
                + ID block has the visual weight it deserves; right column hosts
                the signup card.
                min-w-0 on the container + on each grid child is required:
                grid items default to min-width: auto, so any wide descendant
                (the EmailGate card, long task strings) blows the grid past
                viewport width and clips content on the right. */}
            <div className="grid gap-6 md:gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-12 mb-6 md:mb-10 items-start min-w-0">
              {/* Left Column - Photo & Grass Info */}
              <div className="min-w-0">
                {/* On mobile: vertical stack, centered to match the hero header
                    above. On md+: photo above text in left column.
                    On lg+: text-left within the wider left column. */}
                <div className="flex flex-col items-center text-center md:block md:text-center lg:text-left">
                  {/* Image - takes the full left-column width on desktop so the
                      photo reads as the payoff anchor, not a thumbnail. */}
                  <div className="relative flex-shrink-0 mb-4 md:mb-6 w-full">
                    {referencePhoto ? (
                      // Side-by-side: the user's photo next to our reference
                      // gallery for the matched species so they can verify the ID
                      // visually and tap through curated alternates (blade, seed
                      // head) + zoom. When the peak CTA is up, the pair shrinks to
                      // verification-sized thumbnails on mobile so the digest ask
                      // lands a half-scroll away (desktop keeps the full-width pair).
                      <ReferencePhotoGallery
                        // Key on the species so picking a different grass via
                        // "Not quite" (onSelectAlternative) remounts the gallery
                        // and resets activeIndex/lightbox to the new lead — a stale
                        // index would otherwise drive the thumbnail/counter/analytics
                        // against a shorter image list ("3 / 2").
                        key={analysisResult.grassType}
                        uploadedImage={uploadedImage}
                        images={referenceImages}
                        grassType={analysisResult.grassType}
                        showPeakCta={showPeakCta || showModerateAnonHeroCta}
                      />
                    ) : (
                      <div
                        className="relative w-40 h-40 mx-auto md:w-full md:h-auto md:aspect-square lg:aspect-[4/3] bg-linear-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-lg"
                        style={{ viewTransitionName: 'grass-photo' }}
                      >
                        {uploadedImage && (
                          <img
                            src={uploadedImage}
                            alt="Your grass photo"
                            className="w-full h-full object-cover"
                          />
                        )}
                        {isUnknownOther && uploadedImage && (
                          <span className="absolute left-3 top-3 rounded-full bg-gray-900/65 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                            Your photo
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* The additive photo lane sits directly under the pair it
                      improves (Anthony, 2026-08-30): a second angle is judged
                      against the photos, so the offer belongs next to them,
                      not four viewports down in the verification block. */}
                  {canRetryAdditive && !isUnknownOther && (
                    <div className="mx-auto -mt-1 mb-4 w-full max-w-md text-center md:mb-6" ref={refinementOfferRef}>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRetryAdditive}
                        disabled={refinementPending}
                        className="min-h-[44px] w-full rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
                      >
                        {refinementLocked ? (
                          <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
                        ) : (
                          <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                        )}
                        {retryAdditiveLabel}{refinementLocked && <span className="sr-only">, free account</span>}
                      </Button>
                      {!refinementLocked && refinementPolicy != null && refinementPolicy.refinementsRemaining !== 0 && (
                        <p className="mt-1.5 text-xs text-gray-500">This re-run is free. It will not use another scan.</p>
                      )}
                      {refinementLocked && (
                        <p className="mt-1.5 text-xs text-gray-500">Free account first. Your photo stays right here.</p>
                      )}
                    </div>
                  )}

                  {/* Grass Type & Confidence */}
                  <div className="w-full min-w-0">
                    {/* Grass-name heading — only when the hero didn't already
                        state it. High confidence reads "That's {grass}." and
                        moderate reads "Looks like {grass}.", so repeating it
                        here would be the same name twice. Kept for genuinely
                        low (<50) confidence, whose hero stays nameless. */}
                    {isLowConfidence && (
                      <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 mb-3 leading-tight">
                        {analysisResult.grassType}
                      </h2>
                    )}

                    {isUnknownOther && (
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-left md:p-6">
                        <div className="mb-3.5 flex items-center gap-2.5">
                          <Camera className="h-[18px] w-[18px] flex-none text-green-600" aria-hidden="true" />
                          <span className="text-[15px] font-bold text-gray-900">
                            For a confident ID next time, capture:
                          </span>
                        </div>
                        <ol className="space-y-2.5">
                          {NON_GRASS_CAPTURE_STEPS.map((step, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                              <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-green-100 text-[12px] font-bold text-green-700">
                                {i + 1}
                              </span>
                              <span className="text-[14px] leading-snug text-gray-700">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Compact in-column meter — low band only. Moderate now
                        mirrors the high-confidence layout: the % rides in the
                        hero subhead and the meter lives inside the "Why this
                        ID" box, so a second bar here would duplicate it. */}
                    {isLowConfidence && (
                      <div className="mb-2 md:mb-4">
                        <ConfidenceMeter confidence={effectiveConfidence} caveat={CONFIDENCE_SCORE_CAVEAT} />
                      </div>
                    )}

                    {/* Raw region line — low only; the moderate hero subhead
                        already names the region. */}
                    {isLowConfidence && (
                      <div className="text-xs md:text-sm text-gray-600">
                        {analysisResult.region || "Cool Season Zone"}
                      </div>
                    )}
                  </div>
                </div>

                {/* Low Confidence Warning - full width below the horizontal layout */}
                {isLowConfidence && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 md:p-4 mt-4 md:mt-6">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                        <span className="text-yellow-600 text-sm font-bold">!</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-yellow-800 mb-1">Regional best guess</p>
                        <p className="text-sm text-yellow-700 leading-relaxed">
                          We matched this from your region and photo, but couldn&apos;t fully confirm it visually.
                          Keep it, pick a closer match below, or try a clearer close-up of the blades.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Why this ID — desktop slot, anchored directly under the
                    photo/ID block instead of floating in grid row 2. */}
                {renderWhyThisId && (
                  <div className="hidden lg:block mt-6 min-w-0">{renderWhyThisId(true)}</div>
                )}
              </div>

              {/* Unknown/Other branch — show alternatives picker instead of
                  the care plan card. A choice replaces the canonical identity
                  without changing the model's original score. */}
              {isUnknownOther ? (
                <div className="flex flex-col justify-center gap-4 min-w-0">
                  {rerouteOwnsPhotoLane && renderDiagnosisReroute()}
                  {!deferUnknownPicker || showUnknownPicker ? (
                    <>
                      <UnknownOtherPicker
                        alternatives={analysisResult.alternatives}
                        region={analysisResult.region}
                        onSelect={handleSelectAlternative}
                        pickerContext="unknown_other"
                        showRefinementHint={canRefineWithSupportingPhoto && !rerouteOwnsPhotoLane}
                      />
                      {/* Browsing types must not bury the refinement offer:
                          the primary CTA stays under the picker at full
                          weight. Mobile keeps the verdict-attached copy, so
                          this one renders lg-up only. */}
                      {canRefineWithSupportingPhoto && !rerouteOwnsPhotoLane ? (
                        <div className="hidden flex-col gap-2 lg:flex" ref={refinementOfferRef}>
                          <Button
                            type="button"
                            onClick={handleRefine}
                            disabled={refinementPending}
                            className="min-h-[56px] w-full rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-[17px] font-bold text-white shadow-lg hover:from-green-700 hover:to-emerald-700 hover:shadow-xl"
                          >
                            {refinementLocked ? (
                              <Lock className="mr-2 h-5 w-5" aria-hidden="true" />
                            ) : (
                              <Camera className="mr-2 h-5 w-5" aria-hidden="true" />
                            )}
                            {refinementCtaLabel}{refinementLocked && <span className="sr-only">, free account</span>}
                          </Button>
                          {/* The free-re-run promise requires a live policy: saved pages have no
                        entitlement knowledge, and promising free on a spent root would be
                        false. refinementsRemaining === null means unlimited (premium). */}
                    {!refinementLocked && refinementPolicy != null && refinementPolicy.refinementsRemaining !== 0 && (
                            <p className="text-center text-xs text-gray-500">This re-run is free. It will not use another scan.</p>
                          )}
                          {refinementLocked && (
                            <p className="text-center text-xs text-gray-500">Free account first. Your photo stays right here.</p>
                          )}
                        </div>
                      ) : (
                        <div className="text-center">
                          <button
                            onClick={handleRetakePhoto}
                            className="text-xs text-gray-400 hover:text-gray-500 transition-colors"
                          >
                            &larr; Take another photo
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
    canRefineWithSupportingPhoto && !rerouteOwnsPhotoLane ? (
                      // The one action that can actually fix this result gets
                      // the page's primary treatment; browsing types is the
                      // secondary escape hatch beneath it.
                      <div className="flex flex-col gap-3" ref={refinementOfferRef}>
                        <Button
                          type="button"
                          onClick={handleRefine}
                          disabled={refinementPending}
                          className="hidden min-h-[56px] w-full rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-[17px] font-bold text-white shadow-lg hover:from-green-700 hover:to-emerald-700 hover:shadow-xl lg:flex"
                        >
                          {refinementLocked ? (
                            <Lock className="mr-2 h-5 w-5" aria-hidden="true" />
                          ) : (
                            <Camera className="mr-2 h-5 w-5" aria-hidden="true" />
                          )}
                          {refinementCtaLabel}{refinementLocked && <span className="sr-only">, free account</span>}
                        </Button>
                        {/* The free-re-run promise requires a live policy: saved pages have no
                        entitlement knowledge, and promising free on a spent root would be
                        false. refinementsRemaining === null means unlimited (premium). */}
                    {!refinementLocked && refinementPolicy != null && refinementPolicy.refinementsRemaining !== 0 && (
                          <p className="hidden text-center text-xs text-gray-500 lg:block">This re-run is free. It will not use another scan.</p>
                        )}
                        {refinementLocked && (
                          <p className="hidden text-center text-xs text-gray-500 lg:block">Free account first. Your photo stays right here.</p>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            emitNextAction('browse_grass_types')
                            setShowUnknownPicker(true)
                          }}
                          className="min-h-[44px] w-full rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
                        >
                          <BookOpen className="mr-2 h-4 w-4" aria-hidden="true" />
                          I know my grass, browse types
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={canRefineWithSupportingPhoto ? handleRefine : handleRetakePhoto}
                          disabled={canRefineWithSupportingPhoto && refinementPending}
                          className="min-h-[44px] flex-1 rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
                        >
                          {canRefineWithSupportingPhoto && refinementLocked ? (
                            <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
                          ) : (
                            <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                          )}
                          {canRefineWithSupportingPhoto ? refinementCtaLabel : 'Take another photo'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            emitNextAction('browse_grass_types')
                            setShowUnknownPicker(true)
                          }}
                          className="min-h-[44px] flex-1 rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
                        >
                          <BookOpen className="mr-2 h-4 w-4" aria-hidden="true" />
                          I know my grass, browse types
                        </Button>
                      </div>
                    )
                  )}

                  {/* The mixed case: the turf question kept the photo lane
                      above, so the weed check follows it as the second option. */}
                  {isMixedWeedReroute && renderDiagnosisReroute()}

                  {/* Signed-in redemption of the three-photo promise. Sits at
                      the foot of the recovery actions — under the reroute card
                      and under whatever picker/retake lane rendered — because
                      it is the option of last resort on this column, not a
                      competitor for its primary. */}
                  {showUnknownEntitledAdditive && renderEntitledAdditivePhoto()}

                  {/* Soft account ask — the Unknown/Other branch has no plan to
                      save, but it's still a lawn owner mid-flow; without this
                      it was the only anon result path with zero account ask
                      (2026-07-14). Quiet card, OAuth only (no email gate — there
                      is no plan to send), below the recovery actions so it never
                      competes with the picker/reshoot as the primary next step. */}
                  {!user && (
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-6">
                      <p className="text-[15px] font-bold text-gray-900 mb-1 text-center">
                        Keep track of your lawn anyway
                      </p>
                      <p className="text-[13px] text-gray-500 leading-relaxed mb-3 text-center">
                        A free account saves your scans, reads up to 3 photos per scan for a more confident ID, and picks up where this one left off.
                      </p>
                      <SocialLoginButtons
                        redirectTo={timelineRedirectUrl ?? oauthFallbackRedirectUrl}
                        compact
                        onBeforeRedirect={() => {
                          emitNextAction('create_account')
                          onPrimaryCtaClick?.("oauth")
                          // Own source: this branch has no email lane to join
                          // with, so folding it into grass_id_gate would hide
                          // how much signup the Unknown/Other path produces.
                          stashEmailCaptureAttribution("grass_id_unknown_other")
                        }}
                      />
                    </div>
                  )}

                  <div className="text-center">
                    <AppDownloadTextLink placement={`${entryPath}_grass_id_unknown_other`} />
                  </div>
                </div>
              ) : showCarePlanCTA ? (
                USE_LEGACY_HARD_GATE ? (
                  // Kill-switch path: legacy hard gate (pre-2026-05-13 layout).
                  // Kept for fast rollback if the hybrid soft-entry layout
                  // damages the capture funnel — set
                  // NEXT_PUBLIC_HOMEPAGE_HARD_GATE_FALLBACK=true in Vercel env.
                  <div className="flex flex-col justify-center min-w-0">
                    {user ? (
                      <AuthenticatedCarePlanCTA
                        analysisResult={analysisResult}
                        savedAnalysisId={savedAnalysisId}
                        timelineHref={planTimelineHref}
                        isSubmitting={isSubmittingCarePlan}
                        onViewTimeline={onViewTimeline}
                        onSaveAndViewTimeline={onSaveAndViewTimeline}
                      />
                    ) : emailGateCompleted ? (
                      <CarePlanSentSuccess
                        grassType={analysisResult.grassType}
                        region={analysisResult.region}
                        zipPrefix={zipCode ? zipCode.slice(0, 3) : undefined}
                        email={carePlanEmail || undefined}
                        onSeePlan={onSeeCarePlan}
                        seePlanHref={carePlanHref}
                        oauthRedirectTo={timelineRedirectUrl}
                        onOAuthClick={handlePostCaptureOAuthClick}
                      />
                    ) : (
                      <EmailGate
                        variant="cta"
                        email={carePlanEmail}
                        emailSuggestion={emailSuggestion}
                        isSubmitting={isSubmittingCarePlan}
                        grassType={analysisResult.grassType}
                        region={analysisResult.region}
                        uploadedImage={uploadedImage}
                        timelineRedirectUrl={timelineRedirectUrl}
                        onEmailChange={onEmailChange}
                        onEmailKeyPress={onEmailKeyPress}
                        onSubmit={onEmailSubmit}
                        onAcceptSuggestion={onAcceptEmailSuggestion}
                        onDismissSuggestion={onDismissEmailSuggestion}
                      />
                    )}

                    {!user && isHighConfidence && (
                      <div className="mt-4 rounded-xl border border-gray-100 bg-white px-4 py-3">
                        <div className="text-[13px] text-gray-500 leading-tight text-center">
                          Backed by{" "}
                          <span className="font-bold text-gray-900">USDA Plant Hardiness data</span>
                          <span className="hidden sm:inline">{" "}and 5-year regional soil-temp averages</span>
                          <span className="sm:hidden">.</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Hybrid soft-entry layout (default): right column hosts just
                  // the plan-preview card. Products, tools, and the upgrade ask
                  // render full-width below the grid (see block after the grid
                  // close below).
                  <div className="flex flex-col gap-4 min-w-0 justify-center lg:justify-start lg:row-span-2">
                    {/* Signed-in users' primary action lives in the hero
                        (showAuthedHeroCta) as of 2026-07-11 — a second
                        identical button here read as clutter on desktop where
                        both were in the same viewport. */}
                    {/* Every savable anonymous result gets the same consolidated
                        digest CTA (OAuth ask on top, plan preview, locked months,
                        email fallback), or the success frame once the email gate
                        is completed. 2026-08-08: this was `!user && isHighConfidence`,
                        which dropped 50-69% results (375 anon scans / 30d, 14.8%)
                        to a compact below-grid ask labelled only "Google"/"Apple" —
                        present but unreadable as a save action. Moderate results
                        keep their calibration line via `planHeaderOverride`, so
                        promoting the ask never implies more certainty than we have.
                        Authed users keep the plain plan card; their next step is
                        the timeline, not a save. */}
                    {!user ? (
                      emailGateCompleted ? (
                        <CarePlanSentSuccess
                          grassType={analysisResult.grassType}
                          region={analysisResult.region}
                          zipPrefix={zipCode ? zipCode.slice(0, 3) : undefined}
                          email={carePlanEmail || undefined}
                          onSeePlan={onSeeCarePlan}
                          seePlanHref={carePlanHref}
                          oauthRedirectTo={timelineRedirectUrl}
                          savedPlanContext={{
                            analysisId: savedAnalysisId || undefined,
                            mode: 'identify',
                            planType: 'care',
                            outcome: 'identified',
                            entryPath: 'post_capture_success',
                            subject: analysisResult.grassType,
                          }}
                          onOAuthClick={handlePostCaptureOAuthClick}
                        />
                      ) : (
                        <ResultCarePlanDigest
                          grassType={analysisResult.grassType}
                          region={analysisResult.region}
                          zipCode={zipCode}
                          weekAhead={weekAhead}
                          weekAheadLocationLabel={weekAheadLocationLabel}
                          isHighConfidence={isHighConfidence}
                          planHeaderOverride={
                            !isHighConfidence
                              ? isUserSelectedIdentity
                                ? `Using the grass you selected: ${analysisResult.grassType}.`
                                : `Best guess for your region: ${analysisResult.grassType}.`
                              : undefined
                          }
                          grassNameIsConfirmed={isHighConfidence || isUserSelectedIdentity}
                          oauthShownInHero={showHeroSavePills}
                          email={carePlanEmail}
                          emailSuggestion={emailSuggestion}
                          isSubmitting={isSubmittingCarePlan}
                          timelineRedirectUrl={timelineRedirectUrl ?? oauthFallbackRedirectUrl}
                          savedAnalysisId={savedAnalysisId}
                          onPrimaryCtaClick={onPrimaryCtaClick}
                          onEmailChange={onEmailChange}
                          onEmailKeyPress={onEmailKeyPress}
                          onSubmit={onEmailSubmit}
                          onAcceptSuggestion={onAcceptEmailSuggestion}
                          onDismissSuggestion={onDismissEmailSuggestion}
                        />
                      )
                    ) : (
                      <ResultPlanCard
                        grassType={analysisResult.grassType}
                        region={analysisResult.region}
                        headerOverride={
                          !isHighConfidence
                            ? isUserSelectedIdentity
                              ? `Using the grass you selected: ${analysisResult.grassType}.`
                              : `Best guess for your region: ${analysisResult.grassType}.`
                            : undefined
                        }
                      />
                    )}
                  </div>
                )
              ) : (
                // Low confidence (1-49%) — too uncertain for a care-plan gate,
                // but we still have a real primary grass + regional alternatives,
                // so offer the picker (seeded with the model's primary grass)
                // instead of a retry-only dead end. Mirrors the Unknown/Other
                // branch and the shipped mobile low-confidence pattern. A pick
                // unlocks a user-selected plan without changing confidence.
                // Failed-analysis is handled by the early return above.
                <div className="flex flex-col justify-center min-w-0">
                  <p className="text-sm text-gray-600 mb-3 leading-relaxed text-center">
                    We couldn&apos;t fully confirm this one. Pick the closest match to build your plan, or try a clearer photo.
                  </p>
                  <UnknownOtherPicker
                    alternatives={[analysisResult.grassType, ...(analysisResult.alternatives ?? [])]}
                    region={analysisResult.region}
                    onSelect={handleSelectAlternative}
                    pickerContext="low_confidence"
                    replacementNote={pickerReplacementNote}
                    showRefinementHint={canRefineLowConfidence}
                  />
                  {/* Promoted out of the old text-xs escape hatch. A second
                      photo is the one action that can actually move this
                      score, so it gets an affordance instead of an apology.
                      The restart link lives once, at the bottom of the screen,
                      so this branch renders only the refinement offer. */}
                  {canRefineLowConfidence && (
                    <div className="mx-auto mt-3 w-full max-w-[calc(100%-72px)] text-center sm:max-w-none" ref={refinementOfferRef}>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRefine}
                        disabled={refinementPending}
                        className="min-h-[44px] w-full rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
                      >
                        {refinementLocked ? (
                          <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
                        ) : (
                          <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                        )}
                        {refinementCtaLabel}{refinementLocked && <span className="sr-only">, free account</span>}
                      </Button>
                      {refinementLocked && (
                        <p className="mt-1.5 text-xs text-gray-500">Free account first. Your photo stays right here.</p>
                      )}
                    </div>
                  )}
                  {/* Sub-50 was the only identify outcome with no account ask
                      at all: showCarePlanCTA gates the hero CTA, the sticky
                      bar, the save modal and the whole below-grid block on
                      >=50, so this column rendered a picker and a retry and
                      nothing else. The ask is proportionate to the result —
                      OAuth only, no care plan promised for a grass we have not
                      confirmed, no email gate (there is no plan to send). */}
                  {!user && (
                    <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:p-6">
                      <p className="mb-1 text-center text-[15px] font-bold text-gray-900">
                        Keep this scan while we pin down the ID
                      </p>
                      <p className="mb-3 text-center text-[13px] leading-relaxed text-gray-500">
                        A free account saves this photo and reads up to 3 photos per scan for a more confident match.
                      </p>
                      <SocialLoginButtons
                        redirectTo={timelineRedirectUrl ?? oauthFallbackRedirectUrl}
                        compact
                        onBeforeRedirect={() => {
                          emitNextAction('create_account')
                          onPrimaryCtaClick?.("oauth")
                          // Own source, same reasoning as the Unknown/Other
                          // card: no email lane to join with, and folding it
                          // into grass_id_gate would hide how much signup the
                          // sub-50 path produces.
                          stashEmailCaptureAttribution("grass_id_low_confidence")
                        }}
                      />
                    </div>
                  )}

                  <div className="mt-4 text-center">
                    <AppDownloadTextLink placement={`${entryPath}_grass_id_low_confidence`} />
                  </div>
                </div>
              )}

            </div>

            {/* Product picks come straight after the plan (Anthony,
                2026-08-30): the reasoning box, seasonal ribbon and
                verification read as reference material and follow them. */}
            {showCarePlanCTA && !USE_LEGACY_HARD_GATE && !isUnknownOther && (
              <div className="mt-6 md:mt-8">
                <ResultProductPicks grassType={analysisResult.grassType} />
              </div>
            )}

            {/* Why this ID — mobile/tablet slot, below the product shelf. On
                lg it renders inside the left column under the photo instead. */}
            {renderWhyThisId && <div className="mt-6 min-w-0 lg:hidden">{renderWhyThisId(false)}</div>}

            {belowGridSlot}

            {/* Verification and retry sit directly under the result they are
                about, ahead of products, tools and the app card. At the page
                bottom they were 4+ viewport heights from the verdict on a
                phone, which is the edge-blindness zone (signal-monetization).
                The ranked-candidate list stays at the true bottom: it is
                reference material, not an action. */}
            {!isUnknownOther && (
              <div
                data-testid="result-bottom-verification"
                className="mx-auto mt-6 max-w-3xl space-y-3 border-t border-gray-100 pt-6 md:mt-8 md:pt-8"
              >
                {/* The guide for the grass we actually named. Runner-up
                    candidates have had guide links since the ranked-candidate
                    block shipped, but the identified species had none, and that
                    block is suppressed for user-selected identities — so the
                    one grass the visitor came here about was the only one with
                    no way to read about it. Covers a user-selected identity
                    too: their pick is the identity the page now uses. */}
                {identifiedGuideSlug && (
                  <div className="text-center">
                    <Link
                      href={`/guides/${identifiedGuideSlug}`}
                      data-testid="identified-grass-guide-link"
                      onClick={() => {
                        emitNextAction('open_grass_guide')
                        trackEvent('results_guide_link_clicked', {
                          grass_type: analysisResult.grassType,
                          guide_slug: identifiedGuideSlug,
                          link_context: 'identified_grass',
                          identity_source: analysisResult.identitySource ?? 'model',
                          authenticated: Boolean(user),
                        })
                      }}
                      className="inline-flex min-h-[44px] items-center gap-1 px-2 text-[13px] font-semibold text-green-700 underline underline-offset-[3px] transition-colors hover:text-green-800"
                    >
                      {`Read the ${analysisResult.grassType} care guide`}
                      <ArrowRight className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    </Link>
                  </div>
                )}
                <ResultConfidenceFeedback
                  key={confidenceFeedbackKey}
                  grassType={analysisResult.grassType}
                  confidence={analysisResult.confidence}
                  analysisId={entryPath === "saved_result" ? null : savedAnalysisId}
                  verifyLabel={!isUserSelectedIdentity}
                  onResponse={(positive) => {
                    setShowFeedbackPicker(!positive)
                    if (positive && !isUserSelectedIdentity && entryPath !== "saved_result") {
                      onConfirmIdentity?.()
                    }
                  }}
                  eventProperties={{
                    analysis_id: savedAnalysisId ?? undefined,
                    model: analysisResult.model,
                    pipeline_version: analysisResult.pipelineVersion,
                    identity_source: analysisResult.identitySource ?? 'model',
                    entry_path: entryPath,
                  }}
                />
                {/* 2026-08-08: the moderate band's always-open picker collapsed
                    into this "Not quite" flow — every band >=50 now corrects the
                    same way. Context is band-keyed so the funnel read can
                    segment corrections; "low_confidence" is sub-50-only now. */}
                {showFeedbackPicker && effectiveConfidence >= 50 && (
                  <UnknownOtherPicker
                    alternatives={[analysisResult.grassType, ...(analysisResult.alternatives ?? [])]}
                    region={analysisResult.region}
                    onSelect={handleSelectAlternative}
                    pickerContext={isHighConfidence ? "high_confidence_correction" : "moderate_correction"}
                    replacementNote={pickerReplacementNote}
                    showRefinementHint={canRefineModerate}
                  />
                )}
                {/* Moderate band (50-69): the header says "closest match", not
                    a confirmed ID, so the confirm-it photo offer is honest
                    here. Outline weight — the care plan stays the page's
                    primary conversion on a successful result. */}
                {(canRefineModerate || canRefineConflict) && (
                  <div className="mx-auto w-full max-w-md text-center" ref={refinementOfferRef}>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRefine}
                      disabled={refinementPending}
                      className="min-h-[44px] w-full rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
                    >
                      {refinementLocked ? (
                        <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                      )}
                      {refinementCtaLabel}{refinementLocked && <span className="sr-only">, free account</span>}
                    </Button>
                    {/* The free-re-run promise requires a live policy: saved pages have no
                        entitlement knowledge, and promising free on a spent root would be
                        false. refinementsRemaining === null means unlimited (premium). */}
                    {!refinementLocked && refinementPolicy != null && refinementPolicy.refinementsRemaining !== 0 && (
                      <p className="mt-1.5 text-xs text-gray-500">This re-run is free. It will not use another scan.</p>
                    )}
                    {refinementLocked && (
                      <p className="mt-1.5 text-xs text-gray-500">Free account first. Your photo stays right here.</p>
                    )}
                  </div>
                )}
                {/* The additive lane moved up under the photo pair (2026-08-30);
                    only the full-restart escape hatch stays down here. */}
                <div className="mx-auto w-full max-w-md space-y-2 text-center">
                  {!hideReplacementRetry && (
                    <button
                      type="button"
                      onClick={handleRetakePhoto}
                      className="inline-flex min-h-[44px] items-center justify-center px-3 text-xs text-gray-400 transition-colors hover:text-gray-500"
                    >
                      Try a different photo
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Hybrid soft-entry full-width sections — only render when the
                care-plan CTA is active and the kill switch is off. Products,
                tools, and the upgrade ask (or quick-links for authed users)
                slot in below the grid so they read as additive content rather
                than competing with the grass-result card on the left. */}
            {showCarePlanCTA && !USE_LEGACY_HARD_GATE && !isUnknownOther && (
              <div className="space-y-6 md:space-y-8 mt-6 md:mt-8">
                {/* Honest impermanence cue — ownership loss, not data
                    destruction (the row IS persisted server-side; what the anon
                    user lacks is an account-bound way back to it). */}
                {(showImpermanenceNote || showReturnVisitTreatment) && !user && !emailGateCompleted && !isHighConfidence && (
                  <p className="text-xs text-gray-500 text-center leading-snug">
                    {/* V2 return variant: the saved-result page IS the durable
                        link, so "gone when you close this tab" would be false
                        there — the honest loss is the extra trip back. */}
                    {showReturnVisitTreatment
                      ? `This result isn't saved to an account. You've come back to it ${
                          (returnVisit?.visitCount ?? 2) > 2 ? `${(returnVisit!.visitCount) - 1} times` : "once"
                        } already.`
                      : "This result isn't saved to an account. It's gone when you close this tab."}{" "}
                    <button
                      type="button"
                      onClick={() => {
                        if (showReturnVisitTreatment) {
                          trackEvent("result_return_visit_treatment_clicked", {
                            grass_type: analysisResult.grassType,
                            entry_path: entryPath,
                            visit_count: returnVisit?.visitCount,
                            surface: "impermanence_note",
                          })
                        }
                        onImpermanenceNoteClick?.()
                        document
                          .getElementById("care-plan-upgrade")
                          ?.scrollIntoView({ behavior: "smooth", block: "start" })
                      }}
                      className="font-semibold text-green-700 underline underline-offset-2 hover:text-green-800 transition-colors"
                    >
                      {showReturnVisitTreatment ? "Save it free so it's one tap next time" : "Save it free"}
                    </button>
                  </p>
                )}

                {/* Quota-aware early ask after the anon web allowance is spent. */}
                {!user &&
                  scanUsage &&
                  !scanUsage.isPremium &&
                  Number.isFinite(scanUsage.remaining) &&
                  scanUsage.remaining <= 0 && (
                    <ScanQuotaBanner
                      remaining={scanUsage.remaining}
                      unlockCount={registeredExtraIdentifyScans}
                      onShown={onQuotaWarningShown}
                      onSaveClick={onQuotaWarningClicked}
                    />
                  )}

                {/* Peak-End: the conversion ask sits directly under the result/
                    confidence block — same viewport as the peak — with product
                    picks and tool links demoted below it. One frame, one CTA
                    (mirrors the mobile post-scan collapse, 2026-06-19).
                    ALL anonymous results are handled by ResultCarePlanDigest in
                    the grid column above (2026-08-08), so this below-grid slot
                    now covers authed users only. A second anon ask here would
                    duplicate the digest's OAuth pills. */}
                {user ? (
                  <ResultQuickLinks
                    analysisResult={analysisResult}
                    zipCode={zipCode}
                    uploadedImageUrl={null}
                    savedAnalysisId={savedAnalysisId}
                    isSubmitting={isSubmittingCarePlan}
                    onSaveAndViewTimeline={onSaveAndViewTimeline}
                    onViewTimeline={onViewTimeline}
                  />
                ) : null}

                <ResultToolDeeplinks
                  grassType={analysisResult.grassType}
                  zip={zipCode}
                  onNextAction={() => emitNextAction('open_tool')}
                />

                {/* The one paid continuation of a confirmed ID. Sits after
                    products and tools so the free value lands first, and above
                    the app card so it is not in the page-edge blind zone
                    (signal-monetization). A full-width row with an outlined
                    pill, not a filled button: the save ask above stays the only
                    filled primary action on the screen, but the row is a real
                    tap target instead of a text link. Copy names the outcome
                    and the grass; the price and tier wait for /pricing. */}
                {showPremiumReminderContinuation && (
                  <Link
                    href={premiumContinuationHref}
                    data-testid="result-premium-reminders-link"
                    onClick={() => emitNextAction('premium_reminders')}
                    className="group flex min-h-[44px] items-center gap-3 rounded-xl border border-green-200 bg-white px-3.5 py-3 transition-colors hover:border-green-300 hover:bg-green-50/40"
                  >
                    <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-green-50">
                      <Bell className="h-4 w-4 text-green-700" aria-hidden="true" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="text-[13.5px] font-semibold leading-snug text-gray-900">
                        {`Reminders timed to your ${analysisResult.grassType}`}
                      </span>
                      <span className="text-[11.5px] leading-snug text-gray-500">
                        A heads-up before each window opens.
                      </span>
                    </span>
                    <span className="inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-full border-[1.5px] border-green-700 px-3 py-1.5 text-[12px] font-semibold text-green-700 transition-colors group-hover:bg-green-700 group-hover:text-white">
                      Set up
                    </span>
                  </Link>
                )}

                <AppDownloadCTA
                  placement={appCtaPlacement}
                  variant="card"
                  intent="identify_grass"
                  headline="Scan your grass in the free iPhone app"
                  subhead="Use your camera for a quick grass ID, save this result, and follow your lawn plan from the yard."
                />

              </div>
            )}

            {/* Reference-photo attribution parked at the very bottom (moved off
                the image itself 2026-07-04). Outside the conversion guard so it
                always accompanies the photo when one is shown. CC BY-SA is
                satisfied by same-page attribution — credit EVERY gallery image
                that requires it, not just the lead. */}
            {/* Gate on the images themselves, not a confidence flag: the gallery
                mounts for user-selected IDs at ANY confidence, and CC BY credits
                must follow the photos wherever they render. */}
            {referenceImages.some((img) => img.credit) && (
              <div className="mt-6 border-t border-gray-100 pt-3 text-center">
                {referenceImages
                  .filter((img) => img.credit)
                  .map((img) => (
                    <p key={img.src} className="text-[10px] text-gray-400">
                      Reference photo{img.caption ? ` (${img.caption})` : ''}: {img.credit}{' '}
                      <a
                        href={img.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="underline hover:text-gray-500"
                      >
                        {img.license}
                      </a>
                    </p>
                  ))}
              </div>
            )}

            {/* Ranked possibilities — reference material that belongs after
                the verdict, the actions and the photo credits. */}
            {!isUnknownOther && !isUserSelectedIdentity && analysisResult.candidates && analysisResult.candidates.length > 1 && (
              <div className="mx-auto mt-6 max-w-3xl">
                <RankedCandidateEvidence
                  candidates={analysisResult.candidates}
                  onGuideClick={handleCandidateGuideClick}
                />
              </div>
            )}
      </AnalysisResultFrame>

      {/* Mobile sticky save bar — covers the dead zone between the hero and
          the digest ask for anon users (70% never scrolled the ask into view).
          Fixed-position, so placement in the tree is cosmetic. */}
      {/* Anon moderate-confidence gets the same dead-zone coverage. Since
          2026-08-08 it shares the digest, which owns #care-plan-upgrade for
          every anonymous result, so the bar's scroll/auto-hide wiring and its
          scroll target are unchanged. */}
      {(showPeakCta || showModerateAnonHeroCta) && (
        <ResultStickySaveBar
          itemName={analysisResult.grassType}
          kind="grass"
          entryPath={entryPath}
          onNextAction={() => emitNextAction('save_care_plan')}
          onSaveClick={() => {
            setSaveModalOpenedVia(undefined)
            setSaveModalTrigger("sticky_bar")
          }}
        />
      )}

      {/* Direct save modal — opened by the hero CTA / sticky bar for anon
          savable results. Gated to the same tiers that render those buttons. */}
      {(showPeakCta || showModerateAnonHeroCta) && (
        <ResultSaveModal
          grassNameIsConfirmed={isHighConfidence || isUserSelectedIdentity}
          showUsdaBacking={isHighConfidence}
          open={saveModalTrigger !== null}
          onOpenChange={(open) => {
            if (!open) {
              setSaveModalTrigger(null)
              setSaveModalOpenedVia(undefined)
            }
          }}
          trigger={saveModalTrigger}
          openedVia={saveModalOpenedVia}
          grassType={analysisResult.grassType}
          confidence={analysisResult.confidence}
          entryPath={entryPath}
          email={carePlanEmail}
          emailSuggestion={emailSuggestion}
          isSubmitting={isSubmittingCarePlan}
          timelineRedirectUrl={timelineRedirectUrl ?? oauthFallbackRedirectUrl}
          savedPlanContext={saveFlowContext}
          onPrimaryCtaClick={onPrimaryCtaClick}
          onEmailChange={onEmailChange}
          onEmailKeyPress={onEmailKeyPress}
          onSubmit={onEmailSubmit}
          onAcceptSuggestion={onAcceptEmailSuggestion}
          onDismissSuggestion={onDismissEmailSuggestion}
        />
      )}
    </section>
  )
}

// Authenticated user CTA sub-component
interface AuthenticatedCarePlanCTAProps {
  analysisResult: AnalysisResult
  savedAnalysisId: string | null
  timelineHref: string
  isSubmitting: boolean
  onViewTimeline: () => void
  onSaveAndViewTimeline: () => void
}

function AuthenticatedCarePlanCTA({
  analysisResult,
  savedAnalysisId,
  timelineHref,
  isSubmitting,
  onViewTimeline,
  onSaveAndViewTimeline,
}: AuthenticatedCarePlanCTAProps) {
  return (
    <div className="bg-linear-to-br from-green-50 to-emerald-50 p-8 rounded-2xl border-2 border-green-200">
      <div className="text-center">
        <div className="mb-4">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Your Care Plan is Ready!
        </h3>
        <p className="text-gray-600 mb-6">
          We've created your personalized 12-month lawn care timeline based on your {analysisResult.grassType} analysis.
        </p>

        {/* Primary CTA: View Timeline (with save if not already saved) */}
        {savedAnalysisId ? (
          // Analysis already saved - just navigate to timeline
          <Button asChild size="lg" className="w-full bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition duration-200 min-h-[56px]">
            <Link href={timelineHref} onClick={onViewTimeline}>
              View My Complete Care Plan
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        ) : (
          // Need to save first
          <Button
            onClick={onSaveAndViewTimeline}
            disabled={isSubmitting}
            size="lg"
            className="w-full bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition duration-200 min-h-[56px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                View My Complete Care Plan
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        )}

        <p className="text-sm text-gray-500 mt-3">
          ✓ Saved to your dashboard
        </p>
      </div>
    </div>
  )
}

interface GrassChip {
  label: string
  tone: 'mint' | 'neutral'
}

function getGrassChips(grassType: string, region: string): GrassChip[] {
  const profile = GRASS_CHARACTERISTICS[grassType]
  const chips: GrassChip[] = []

  if (profile?.season === 'Cool') {
    chips.push({ label: 'Cool season', tone: 'mint' })
  } else if (profile?.season === 'Warm') {
    chips.push({ label: 'Warm season', tone: 'mint' })
  }

  if (region) {
    chips.push({ label: region, tone: 'neutral' })
  }

  if (profile?.season === 'Cool') {
    chips.push({ label: 'Spring & fall growth', tone: 'neutral' })
  } else if (profile?.season === 'Warm') {
    chips.push({ label: 'Summer growth', tone: 'neutral' })
  }

  return chips
}

function seasonLabel(grassType: string): string {
  const profile = GRASS_CHARACTERISTICS[grassType]
  if (profile?.season === 'Warm') return 'Warm-season'
  if (profile?.season === 'Cool') return 'Cool-season'
  return 'Common'
}

function idReasoningFor(grassType: string, modelAnalysis?: string): string {
  // Prefer the model's OWN reasoning about the user's actual photo. The static
  // sentence below is a fallback only — it's built from generic
  // GRASS_CHARACTERISTICS, so it is phrased as what's "typical" for the species
  // rather than as something we observed in this image (honesty: don't claim a
  // photo-specific observation we didn't make).
  const trimmed = typeof modelAnalysis === 'string' ? modelAnalysis.trim() : ''
  // Strip em-dashes from the model's own prose (house style: no em-dashes).
  if (trimmed.length >= 20) return trimmed.replace(/\s*—\s*/g, ", ")
  const profile = GRASS_CHARACTERISTICS[grassType]
  if (!profile) {
    return `${grassType} is identified by blade width, texture, and color.`
  }
  return `${grassType} typically has ${profile.bladeWidth.toLowerCase()} blades, ${profile.texture.toLowerCase()} texture, and ${profile.color.toLowerCase()}. That's the basis for this match.`
}

// Split the model's single-blob analysis into readable paragraphs (~2 sentences
// each) for the expanded view. Abbreviation-aware so "St. Augustinegrass",
// "e.g.", "i.e." don't get split mid-sentence.
function analysisParagraphs(text: string, perPara = 2): string[] {
  const ABBR = /(?:^|\s)(?:St|Dr|Mr|Mrs|Ms|vs|etc|approx|no|fig|e\.g|i\.e)\.$/i
  // No lookbehind here: Safari <16.4 throws a parse-time SyntaxError on
  // `(?<=...)`, which kills this whole chunk. Capture the boundary and
  // rebuild instead.
  const normalized = text.replace(/\s+/g, " ").trim()
  const raw: string[] = []
  let start = 0
  const boundary = /([.!?])\s+(?=["'A-Z0-9])/g
  let bm: RegExpExecArray | null
  while ((bm = boundary.exec(normalized)) !== null) {
    raw.push(normalized.slice(start, bm.index + bm[1].length))
    start = bm.index + bm[0].length
  }
  raw.push(normalized.slice(start))
  const sentences: string[] = []
  for (const part of raw) {
    const prev = sentences[sentences.length - 1]
    if (prev && ABBR.test(prev)) sentences[sentences.length - 1] = `${prev} ${part}`
    else sentences.push(part)
  }
  const paras: string[] = []
  for (let i = 0; i < sentences.length; i += perPara) {
    paras.push(sentences.slice(i, i + perPara).join(" "))
  }
  return paras.length ? paras : [text]
}

// REGION_FALLBACKS — when the model returns no alternatives (rare but
// possible), seed the picker with sensible regional defaults so the user
// still has something to pick. Keys match the leading word in the climate
// zone string (e.g. "Southeast - Warm Season" → "Southeast").
function RankedCandidateEvidence({
  candidates,
  onGuideClick,
}: {
  candidates: { name: string; probability: number }[]
  onGuideClick: (candidateName: string, candidateSlug: string) => void
}) {
  const ranked = candidates.slice(0, 3)

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3 text-left">
      <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
        Other close matches we checked
      </p>
      <ol className="mt-2 divide-y divide-gray-200/80">
        {ranked.map((candidate, index) => {
          const profile = getGrassCharacteristics(candidate.name)
          const cue = profile?.photoIdCues?.[0]
          // Null slug means no guide page exists for this candidate — render
          // plain text rather than a dead /guides/ link.
          const slug = grassTypeToGuideSlug(candidate.name)
          return (
            <li key={`${candidate.name}-${index}`} className="flex gap-3 py-2 first:pt-0 last:pb-0">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-gray-600 ring-1 ring-gray-200">
                {index + 1}
              </span>
              <div className="min-w-0">
                {slug ? (
                  <Link
                    href={`/guides/${slug}`}
                    onClick={() => onGuideClick(candidate.name, slug)}
                    className="inline-flex items-center gap-1 text-[13px] font-semibold text-green-700 underline underline-offset-2 hover:text-green-800 transition-colors"
                  >
                    {candidate.name}
                    <ArrowRight className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                  </Link>
                ) : (
                  <p className="text-[13px] font-semibold text-gray-900">{candidate.name}</p>
                )}
                {cue && <p className="mt-0.5 text-xs leading-relaxed text-gray-600">Look for: {cue}</p>}
              </div>
            </li>
          )
        })}
      </ol>
      <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
        These are our best guesses in order, not exact odds. Open a guide to compare, or use “Not quite” to correct the result.
      </p>
    </div>
  )
}

const REGION_FALLBACKS: Record<string, string[]> = {
  Northeast: ['Kentucky Bluegrass', 'Tall Fescue', 'Perennial Ryegrass', 'Mixed Cool-Season Blend'],
  'Mid-Atlantic': ['Tall Fescue', 'Kentucky Bluegrass', 'Zoysiagrass', 'Mixed Cool-Season Blend'],
  Southeast: ['Bermudagrass', 'St. Augustinegrass', 'Centipede', 'Annual Ryegrass'],
  'Great Lakes': ['Kentucky Bluegrass', 'Fine Fescue', 'Perennial Ryegrass', 'Mixed Cool-Season Blend'],
  'Upper Midwest': ['Kentucky Bluegrass', 'Fine Fescue', 'Tall Fescue'],
  Central: ['Tall Fescue', 'Bermudagrass', 'Zoysiagrass'],
  'South Central': ['Bermudagrass', 'St. Augustinegrass', 'Buffalograss', 'Annual Ryegrass'],
  'Mountain West': ['Kentucky Bluegrass', 'Fine Fescue', 'Buffalograss'],
  'West Coast': ['Tall Fescue', 'Bermudagrass', 'Kikuyugrass', 'Kentucky Bluegrass'],
  'Pacific Northwest': ['Perennial Ryegrass', 'Tall Fescue', 'Fine Fescue'],
  'California': ['Tall Fescue', 'Bermudagrass', 'Kikuyugrass', 'St. Augustinegrass'],
  'Desert Southwest': ['Bermudagrass', 'Zoysiagrass', 'St. Augustinegrass'],
  'Florida': ['St. Augustinegrass', 'Zoysiagrass', 'Bahiagrass'],
}

export function UnknownOtherPicker({
  alternatives,
  region,
  onSelect,
  pickerContext,
  showRefinementHint = false,
  replacementNote = "This will replace the model's abstention for your care plan.",
}: {
  alternatives: string[] | undefined
  region: string
  onSelect: (grassType: string) => void
  pickerContext: string
  /** Names the picker-vs-photo tradeoff when a refinement CTA is on the same
   *  screen: picking asserts an identity, the photo verifies one. */
  showRefinementHint?: boolean
  /** One line naming what a confirmation overwrites. The default is the
   *  Unknown/Other wording and is wrong anywhere the model DID name a species,
   *  so every positive-prediction entry point passes its own. */
  replacementNote?: string
}) {
  const [pendingChoice, setPendingChoice] = useState<string | null>(null)
  // Filter to alternatives we have full GRASS_CHARACTERISTICS coverage for —
  // selecting a key with no profile would produce a blank deliverables card
  // downstream. If the model returned junk or nothing, fall back to a regional
  // default list so the picker is never empty.
  const regionKey = region?.split(' - ')[0] ?? ''
  const fallback = REGION_FALLBACKS[regionKey] ?? ['Tall Fescue', 'Kentucky Bluegrass', 'Bermudagrass']
  const candidates = (alternatives && alternatives.length > 0 ? alternatives : fallback)
    .filter((name) => name in GRASS_CHARACTERISTICS)

  // Region-based closest matches first…
  const primary = candidates.length > 0 ? candidates : fallback.filter((n) => n in GRASS_CHARACTERISTICS)
  // …then every other grass type in the catalog. If they scrolled all the way
  // to the picker, let them choose ANY type, not just the regional best guesses.
  const others = Object.keys(GRASS_CHARACTERISTICS).filter((n) => !primary.includes(n))

  // Engagement denominator: fire once when the picker becomes visible so the
  // shown -> picked conversion is measurable by context. Pairs with the
  // unknown_other_alternative_picked event (which carries the same contexts).
  useEffect(() => {
    trackEvent('picker_shown', {
      picker_context: pickerContext,
      alternatives_count: primary.length + others.length,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderOption = (name: string) => {
    const profile = GRASS_CHARACTERISTICS[name]
    const season = profile?.season === 'Cool' ? 'Cool season' : profile?.season === 'Warm' ? 'Warm season' : null
    // Per-grass blade illustration (the same custom SVGs used on the guides
    // cards), keyed by guide slug. Falls back to the leaf when a type has no
    // illustration yet.
    const slug = grassTypeToGuideSlug(name)
    const Blade = slug ? GRASS_BLADES[slug] : undefined
    const isPending = pendingChoice === name
    return (
      <div key={name} className="rounded-xl">
        <button
          type="button"
          onClick={() => setPendingChoice(name)}
          aria-pressed={isPending}
          className={cn(
            'group flex w-full items-center gap-3 rounded-xl border bg-white px-4 py-3 text-left',
            'hover:border-green-300 hover:bg-green-50/40 hover:shadow-sm',
            'focus:outline-hidden focus:ring-2 focus:ring-green-500/30 transition',
            isPending ? 'border-green-400 bg-green-50/60 ring-2 ring-green-500/20' : 'border-gray-200',
          )}
        >
        <div className="flex-shrink-0 flex items-center justify-center w-16 h-14 rounded-xl bg-green-50 border border-green-100 overflow-hidden">
          {Blade ? (
            <Blade color="#16a34a" className="w-full h-full" />
          ) : (
            <Leaf className="w-7 h-7 text-green-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-[15px]">{name}</span>
            {season && (
              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 border border-green-100 rounded-full px-2 py-0.5">
                {season}
              </span>
            )}
          </div>
          {profile && (
            <div className="text-[12.5px] text-gray-600 mt-1 leading-snug">
              {profile.bladeWidth} blades, {profile.color.toLowerCase()}.
            </div>
          )}
        </div>
          {isPending ? (
            <Check className="w-4 h-4 text-green-600 flex-shrink-0" aria-hidden="true" />
          ) : (
            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-green-600 transition-colors flex-shrink-0" />
          )}
        </button>
        {isPending && (
          <div className="mt-2 rounded-xl border border-green-200 bg-green-50/70 p-3">
            <p className="text-sm font-semibold text-gray-900">Use {name} for this lawn?</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              Confirm only if these traits match what you see. {replacementNote}
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                onClick={() => onSelect(name)}
                className="min-h-[44px] flex-1 bg-green-600 text-white hover:bg-green-700"
              >
                Confirm {name}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingChoice(null)}
                className="min-h-[44px] flex-1"
              >
                Keep looking
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-green-100 bg-white p-4 md:p-6 shadow-[0_16px_48px_-16px_rgba(22,163,74,0.18),0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.06em] text-green-700 mb-1.5">
        Closest matches
      </div>
      <div className="text-sm text-gray-600 mb-4 leading-relaxed">
        Based on your region{regionKey ? ` (${regionKey})` : ''}. Pick the one that looks most like your lawn.
        {showRefinementHint ? ' Not sure? Add the photo instead and we will confirm it.' : ''}
      </div>
      <div className="flex flex-col gap-2">
        {primary.map(renderOption)}
      </div>
      {others.length > 0 && (
        <>
          <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.06em] text-green-700 mt-5 mb-3">
            Or pick any grass type
          </div>
          <div className="flex flex-col gap-2">
            {others.map(renderOption)}
          </div>
        </>
      )}
    </div>
  )
}

// Quiet end-of-page reward: a sprout badge that pops in once the bottom of the
// result scrolls into view. Reduced-motion is handled globally (animation
// durations zeroed), so it simply appears for those users.
function EndOfResultFlourish() {
  // Callback ref via state so the observer effect re-runs once the node mounts.
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (!node) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          io.disconnect()
        }
      },
      { threshold: 0.6 },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [node])

  return (
    <div ref={setNode} className="flex flex-col items-center gap-1.5 pt-1 text-center">
      <span
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-full bg-green-50 text-green-600 opacity-0",
          shown && "opacity-100 animate-scale-in",
        )}
      >
        <Leaf className="h-4 w-4" />
      </span>
      <span
        className={cn(
          "text-[11px] text-gray-400 transition-opacity duration-700",
          shown ? "opacity-100" : "opacity-0",
        )}
      >
        That&apos;s your lawn, sorted.
      </span>
    </div>
  )
}

function ResultChip({
  children,
  tone,
  index,
}: {
  children: React.ReactNode
  tone: 'mint' | 'neutral'
  /** When set, the chip scales in with a per-index stagger so the chips read
      as discrete facts arriving in sequence. Reduced-motion is handled by the
      global keyframe-duration reset in globals.css. */
  index?: number
}) {
  const palette =
    tone === 'mint'
      ? 'bg-green-50 text-green-700 border-green-100'
      : 'bg-gray-50 text-gray-600 border-gray-200'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold',
        palette,
        index !== undefined && 'animate-scale-in',
      )}
      style={index !== undefined ? { animationDelay: `${index * 60}ms`, animationFillMode: 'backwards' } : undefined}
    >
      {children}
    </span>
  )
}
